import React, { useEffect, useState, useCallback } from "react";
import styled, { createGlobalStyle } from "styled-components";
import axios from "axios";

/* ─── 전역 스타일 (SajuTester.tsx 동일 CSS vars) ────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg-page: #F8F9FA;
    --bg-card: #FFFFFF;
    --border-light: #E9ECEF;
    --text-primary: #222222;
    --text-secondary: #555555;
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.08);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.1);
    --oe-목: #2E86AB;
    --oe-화: #E63946;
    --oe-토: #F4A261;
    --oe-금: #A8A8A8;
    --oe-수: #1D3557;
  }
  body {
    font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: var(--bg-page);
    color: var(--text-primary);
    font-size: 15px;
  }
  * { font-size: max(15px, 1em); color: inherit; }
  svg text, svg tspan { font-size: revert; }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: #F1F3F4; }
  ::-webkit-scrollbar-thumb { background: #CED4DA; border-radius: 4px; }
`;

const fmtHour = (h: number | null | undefined, m?: number | null) => {
  if (h == null || h === 99) return "시간 모름";
  return m ? `${h}시 ${String(m).padStart(2,"0")}분` : `${h}시`;
};

/* ─── 레이아웃 ────────────────────────────────────────────────────────── */
const Layout = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  position: relative;
`;

/* ─── 사이드바 래퍼 (접기/펼치기 애니메이션) ────────────────────────── */
const SidebarWrap = styled.div<{$open: boolean}>`
  position: relative;
  flex-shrink: 0;
  width: ${p => p.$open ? "280px" : "0"};
  transition: width 0.28s cubic-bezier(.4,0,.2,1);
  overflow: hidden;
`;
const Sidebar = styled.div`
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 280px;
  background: #1A1A2E;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
/* CollapseBtn — SidebarWrap 밖, Layout 기준 absolute */
const CollapseBtn = styled.button<{$open: boolean}>`
  position: absolute;
  left: ${p => p.$open ? "280px" : "0"};
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 56px;
  background: #1A1A2E;
  border: none;
  border-radius: 0 6px 6px 0;
  color: rgba(255,255,255,0.75);
  font-size: 11px;
  cursor: pointer;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 0 6px rgba(0,0,0,0.25);
  transition: left 0.28s cubic-bezier(.4,0,.2,1);
  &:hover { background: #2d2d50; color: #fff; }
`;
const SideHeader = styled.div`
  padding: 20px 16px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
`;
const SideTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 10px;
`;
const RefreshBtn = styled.button`
  font-size: 12px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  color: #A5D8FF;
  border-radius: 6px;
  padding: 4px 12px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.14); }
`;
const ClientList = styled.div`
  overflow-y: auto;
  flex: 1;
`;
const ClientItem = styled.div<{ $active?: boolean; $status?: string }>`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  cursor: pointer;
  background: ${p => p.$active ? "rgba(255,255,255,0.1)" : "transparent"};
  border-left: 3px solid ${p =>
    p.$status === "completed" ? "#51CF66" :
    p.$status === "in_progress" ? "#FF922B" : "#495057"};
  &:hover { background: rgba(255,255,255,0.07); }
`;
const ClientName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const ClientMeta = styled.div`
  font-size: 11.5px;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
`;
const StatusBadge = styled.span<{ $status: string }>`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${p => p.$status === "completed" ? "#1a4a2a" : p.$status === "in_progress" ? "#4a2a00" : "#2a2a3a"};
  color: ${p => p.$status === "completed" ? "#69DB7C" : p.$status === "in_progress" ? "#FFA94D" : "#868E96"};
`;

/* ─── 메인 영역 ───────────────────────────────────────────────────────── */
const Main = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--bg-page);
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
  color: var(--text-secondary);
  font-size: 15px;
`;

/* ─── 프로필 헤더 (SajuTester ProfileHeader 동일) ───────────────────── */
const ProfileHeader = styled.div`
  background: #1A1A2E;
  color: #fff;
  padding: 24px 24px 20px;
`;
const ProfileName = styled.div`
  font-size: 20px;
  font-weight: 800;
  margin-bottom: 4px;
`;
const ProfileMeta = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.6);
  line-height: 1.8;
`;

/* ─── 탭바 (SajuTester TabBar/Tab 동일) ────────────────────────────── */
const TabBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid var(--border-light);
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const Tab = styled.button<{ $active: boolean; $color: string }>`
  padding: 12px 18px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: ${p => p.$active ? "700" : "500"};
  color: ${p => p.$active ? p.$color : "var(--text-secondary)"};
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2.5px solid ${p => p.$active ? p.$color : "transparent"};
  font-family: inherit;
  transition: all 0.15s;
`;

/* ─── 결과 카드 (SajuTester ResultCard 동일) ───────────────────────── */
const ResultCard = styled.div`
  background: var(--bg-card);
  border-radius: 16px;
  margin: 12px 16px;
  padding: 18px 20px;
  box-shadow: var(--shadow-sm);
`;
const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* ─── 내담자 정보 그리드 ─────────────────────────────────────────────── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
`;
const InfoChip = styled.div`
  background: #F8F9FA;
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 10px 12px;
`;
const InfoLabel = styled.div`font-size: 11px; color: var(--text-secondary); margin-bottom: 3px;`;
const InfoValue = styled.div`font-size: 14px; font-weight: 600; color: var(--text-primary);`;

/* ─── 버튼 ────────────────────────────────────────────────────────────── */
const BtnRow = styled.div`display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px;`;
const Btn = styled.button<{ $variant?: string }>`
  padding: 9px 18px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
  background: ${p =>
    p.$variant === "primary" ? "#1A1A2E" :
    p.$variant === "success" ? "#2F9E44" :
    p.$variant === "outline" ? "transparent" : "#F1F3F5"};
  color: ${p =>
    p.$variant === "primary" || p.$variant === "success" ? "#fff" :
    p.$variant === "outline" ? "#1A1A2E" : "var(--text-primary)"};
  border: ${p => p.$variant === "outline" ? "1.5px solid #DEE2E6" : "none"};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const MsgLine = styled.div`font-size: 12px; color: #2F9E44; margin-top: 8px;`;



/* ─── 태그 ────────────────────────────────────────────────────────────── */
const TagRow = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;`;
const Tag = styled.span<{ $type?: "good" | "bad" | "neutral" | "blue" }>`
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  background: ${p =>
    p.$type === "good" ? "#EBFBEE" :
    p.$type === "bad" ? "#FFF5F5" :
    p.$type === "blue" ? "#E7F5FF" : "#F1F3F5"};
  color: ${p =>
    p.$type === "good" ? "#2F9E44" :
    p.$type === "bad" ? "#C92A2A" :
    p.$type === "blue" ? "#1971C2" : "var(--text-secondary)"};
  border: 1px solid ${p =>
    p.$type === "good" ? "#B2F2BB" :
    p.$type === "bad" ? "#FFCCCC" :
    p.$type === "blue" ? "#A5D8FF" : "var(--border-light)"};
`;
const SubLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: 0.04em;
  margin: 10px 0 6px;
`;

/* ─── 대운별 운세 카드 ──────────────────────────────────────────────── */
const DaeunCard = styled.div`
  background: #F8F9FA;
  border: 1px solid var(--border-light);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 10px;
`;
const DaeunHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;
const DaeunGanJiBox = styled.div<{ $isYong?: boolean; $isGi?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${p => p.$isYong ? "#1A1A2E" : p.$isGi ? "#C92A2A" : "#F1F3F5"};
  color: ${p => (p.$isYong || p.$isGi) ? "#fff" : "var(--text-primary)"};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 800;
  flex-shrink: 0;
  line-height: 1.2;
`;
const DaeunMeta = styled.div`flex: 1;`;
const DaeunTitle = styled.div`font-size: 15px; font-weight: 700; color: var(--text-primary);`;
const DaeunSubtitle = styled.div`font-size: 12px; color: var(--text-secondary); margin-top: 2px;`;
const DaeunYongGi = styled.span<{ $isYong?: boolean; $isGi?: boolean }>`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${p => p.$isYong ? "#E7F5FF" : p.$isGi ? "#FFF5F5" : "#F1F3F5"};
  color: ${p => p.$isYong ? "#1971C2" : p.$isGi ? "#C92A2A" : "var(--text-secondary)"};
`;

/* ─── 대운 내 점수 표 ────────────────────────────────────────────────── */
const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 5px;
  margin-bottom: 12px;
`;
const ScoreCell = styled.div<{ $score: number }>`
  text-align: center;
  background: #fff;
  border: 1px solid var(--border-light);
  border-radius: 10px;
  padding: 8px 4px;
  border-top: 3px solid ${p =>
    p.$score >= 70 ? "#2F9E44" :
    p.$score >= 50 ? "#FF922B" : "#E03131"};
`;
const ScoreLabel = styled.div`font-size: 11px; color: var(--text-secondary); margin-bottom: 4px;`;
const ScoreNum = styled.div<{ $score: number }>`
  font-size: 20px;
  font-weight: 800;
  color: ${p => p.$score >= 70 ? "#2F9E44" : p.$score >= 50 ? "#FF922B" : "#E03131"};
`;
const ScoreBar = styled.div<{ $score: number }>`
  height: 3px;
  background: #F1F3F5;
  border-radius: 2px;
  margin-top: 5px;
  overflow: hidden;
  &::after {
    content: "";
    display: block;
    height: 100%;
    width: ${p => p.$score}%;
    background: ${p => p.$score >= 70 ? "#2F9E44" : p.$score >= 50 ? "#FF922B" : "#E03131"};
    border-radius: 2px;
  }
`;

/* ─── 이유 문장 ─────────────────────────────────────────────────────── */
const ReasonList = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const ReasonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #fff;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 8px 12px;
`;
const ReasonKey = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  min-width: 26px;
  padding-top: 1px;
`;
const ReasonText = styled.span`
  font-size: 12.5px;
  color: var(--text-primary);
  line-height: 1.6;
`;

/* ─── 세운별 운세 ─────────────────────────────────────────────────────── */
const SaeunToggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F1F3F5;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  margin-top: 10px;
  &:hover { background: #E9ECEF; }
`;
const SaeunGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 8px;
`;
const SaeunCard = styled.div<{ $isYong?: boolean; $isGi?: boolean }>`
  background: #fff;
  border: 1px solid var(--border-light);
  border-left: 3px solid ${p => p.$isYong ? "#2F9E44" : p.$isGi ? "#E03131" : "#ADB5BD"};
  border-radius: 8px;
  padding: 8px 10px;
`;
const SaeunYearLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 5px;
`;
const SaeunScoreRow = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 5px;
`;
const SaeunScorePill = styled.span<{ $score: number }>`
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  background: ${p => p.$score >= 70 ? "#EBFBEE" : p.$score >= 50 ? "#FFF3BF" : "#FFF5F5"};
  color: ${p => p.$score >= 70 ? "#2F9E44" : p.$score >= 50 ? "#E67700" : "#E03131"};
  font-weight: 600;
`;
const SaeunEventList = styled.div`
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

/* ─── 운세 타임라인 (대운/연운/월운 pillar 카드) ──────────────────────── */
const PillarTimelineWrap = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 6px;
  padding: 4px 0 10px;
  flex-direction: row-reverse;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #CED4DA; border-radius: 2px; }
`;
const PillarCardWrap = styled.div<{ $selected?: boolean; $current?: boolean }>`
  flex-shrink: 0;
  width: 62px;
  border-radius: 12px;
  border: 2px solid ${p => p.$selected ? "#FF922B" : p.$current ? "#339AF0" : "#DEE2E6"};
  background: ${p => p.$selected ? "#FFF4E6" : "#FFFFFF"};
  padding: 7px 4px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transition: all .15s;
  &:hover { border-color: #FF922B; background: #FFF9F0; }
`;
const PillarTopLabel = styled.div`
  font-size: 9px;
  color: var(--text-secondary);
  text-align: center;
  font-weight: 600;
  line-height: 1.3;
`;
const PillarStarLabel = styled.div`
  font-size: 9px;
  color: var(--text-secondary);
  text-align: center;
`;
const PillarGanBlock = styled.div<{ $oe: string }>`
  width: 48px;
  padding: 5px 2px;
  border-radius: 8px;
  background: ${p => OE_BG_A[p.$oe] || "#F8F9FA"};
  border: 1px solid ${p => (OE_COLOR_A[p.$oe] || "#DEE2E6") + "50"};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;
const PillarHangul = styled.div<{ $oe: string }>`
  font-size: 14px;
  font-weight: 800;
  color: ${p => OE_COLOR_A[p.$oe] || "var(--text-primary)"};
  line-height: 1;
`;
const PillarHanja = styled.div<{ $oe: string }>`
  font-size: 9px;
  color: ${p => OE_COLOR_A[p.$oe] || "var(--text-secondary)"};
  opacity: 0.75;
`;
const PillarBottomLabel = styled.div`
  font-size: 8px;
  color: #ADB5BD;
  text-align: center;
`;
const TimelineSection = styled.div`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-light);
`;
const TimelineSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 8px;
  letter-spacing: .3px;
`;

/* ─── 상담사 코멘트 textarea ─────────────────────────────────────────── */
const Textarea = styled.textarea`
  width: 100%;
  background: #F8F9FA;
  border: 1.5px solid var(--border-light);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  padding: 12px 14px;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  &:focus { outline: none; border-color: #ADB5BD; }
`;

/* ─── 오행/간지 정적 데이터 (SajuTester.tsx 동일) ──────────────────── */
const OHAENG_STYLE: Record<string, { color: string }> = {
  목: { color: "#2E86AB" }, 화: { color: "#E63946" }, 토: { color: "#F4A261" },
  금: { color: "#A8A8A8" }, 수: { color: "#1D3557" },
};
const OE_COLOR_A: Record<string,string> = {
  목:"#2E86AB", 화:"#E63946", 토:"#F4A261", 금:"#A8A8A8", 수:"#1D3557",
};
const OE_BG_A: Record<string,string> = {
  목:"#EBF4F9", 화:"#FDE8EA", 토:"#FEF4E8", 금:"#F5F5F5", 수:"#E8EDF3",
};
const OE_LABEL_A: Record<string,string> = {목:"목(木)",화:"화(火)",토:"토(土)",금:"금(金)",수:"수(水)"};
const OE_ORDER_A = ["목","화","토","금","수"] as const;

function extractOePctA(wx: any): Record<string,number> {
  const extAn = wx?.extended_analysis;
  const dmPct = extAn?.diagram_matrix?.opt_on_on?.pct as Record<string,number>|undefined;
  const ftPct = extAn?.forceteller_ohaeng?.pct    as Record<string,number>|undefined;
  const src = dmPct || ftPct;
  if (src && Object.keys(src).length) {
    return Object.fromEntries(OE_ORDER_A.map(o=>[o, Math.round(src[o]||0)]));
  }
  const vals = OE_ORDER_A.map(k=>(typeof wx?.[k]==="number"?wx[k]:0));
  const total = vals.reduce((a,b)=>a+b,0)||1;
  return Object.fromEntries(OE_ORDER_A.map((k,i)=>[k, Math.round(vals[i]/total*100)]));
}

function OePentagonAdmin({wx, dayOe, uid, pct: pctOverride}: {wx:any; dayOe:string; uid:string; pct?: Record<string,number>}) {
  const oePct = pctOverride || extractOePctA(wx);
  const SZ=380, CX=190, CY=190, R=142, NR=48;
  const dayOeIdx = OE_ORDER_A.indexOf(dayOe as typeof OE_ORDER_A[number]);
  const pts = OE_ORDER_A.map((_,i)=>{
    const a=((i - dayOeIdx)*72-90)*Math.PI/180;
    return {x:CX+R*Math.cos(a), y:CY+R*Math.sin(a)};
  });
  const edgePt=(si:number,ei:number,off:number)=>{
    const s=pts[si],e=pts[ei],dx=e.x-s.x,dy=e.y-s.y,len=Math.sqrt(dx*dx+dy*dy);
    return {x:s.x+(dx/len)*off, y:s.y+(dy/len)*off};
  };
  const dist=(si:number,ei:number)=>{
    const s=pts[si],e=pts[ei];
    return Math.sqrt((e.x-s.x)**2+(e.y-s.y)**2);
  };
  const CYCLE5=["목","화","토","금","수"];
  const gen5=(a:string,b:string)=>CYCLE5[(CYCLE5.indexOf(a)+1)%5]===b;
  const ctl5=(a:string,b:string)=>CYCLE5[(CYCLE5.indexOf(a)+2)%5]===b;
  const relLabel=(oe:string)=>{
    if(oe===dayOe)return"비겁";
    if(gen5(oe,dayOe))return"인성";
    if(gen5(dayOe,oe))return"식상";
    if(ctl5(dayOe,oe))return"재성";
    return"관성";
  };
  const genArr=OE_ORDER_A.map((_,i)=>{const ni=(i+1)%5;return{s:edgePt(i,ni,NR+6),e:edgePt(i,ni,dist(i,ni)-NR-6)};});
  const ctlArr=OE_ORDER_A.map((_,i)=>{const ni=(i+2)%5;return{s:edgePt(i,ni,NR+6),e:edgePt(i,ni,dist(i,ni)-NR-6)};});
  const mBId=`mBa${uid}`, mRId=`mRa${uid}`;
  return (
    <svg viewBox={`0 0 ${SZ} ${SZ}`} style={{width:"100%",height:"auto",display:"block"}}>
      <defs>
        <marker id={mBId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <polygon points="0 0,8 4,0 8" fill="#5B9BD5"/>
        </marker>
        <marker id={mRId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <polygon points="0 0,8 4,0 8" fill="#D05050"/>
        </marker>
      </defs>
      {ctlArr.map((a,i)=><line key={`c${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#D05050" strokeWidth={1.8} markerEnd={`url(#${mRId})`}/>)}
      {genArr.map((a,i)=><line key={`g${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#5B9BD5" strokeWidth={1.8} markerEnd={`url(#${mBId})`}/>)}
      {OE_ORDER_A.map((oe,i)=>{
        const p=pts[i], oeP=oePct[oe]||0, isDay=oe===dayOe;
        const fillH=NR*2*Math.min(oeP,100)/100;
        const cpId=`cp${i}a${uid}`;
        return(
          <g key={oe}>
            <clipPath id={cpId}><rect x={p.x-NR} y={p.y+NR-fillH} width={NR*2} height={fillH+2}/></clipPath>
            <circle cx={p.x} cy={p.y} r={NR} fill={OE_BG_A[oe]} stroke={isDay?OE_COLOR_A[oe]:"#D8D8D8"} strokeWidth={isDay?3:1.5}/>
            <circle cx={p.x} cy={p.y} r={NR-1} fill={OE_COLOR_A[oe]} opacity={0.25} clipPath={`url(#${cpId})`}/>
            <text x={p.x} y={p.y-12} textAnchor="middle" fontSize={11} fontWeight={700} fill={OE_COLOR_A[oe]}>
              {OE_LABEL_A[oe].split("(")[0]}({relLabel(oe)})
            </text>
            <text x={p.x} y={p.y+9} textAnchor="middle" fontSize={14} fontWeight={800} fill={OE_COLOR_A[oe]}>
              {oeP}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}
const STEM_OHAENG: Record<string, string> = {
  甲:"목",乙:"목",丙:"화",丁:"화",戊:"토",己:"토",庚:"금",辛:"금",壬:"수",癸:"수",
};
const BRANCH_OHAENG: Record<string, string> = {
  寅:"목",卯:"목",巳:"화",午:"화",辰:"토",戌:"토",丑:"토",未:"토",申:"금",酉:"금",亥:"수",子:"수",
};
const STEM_INFO: Record<string, { hangul: string; yinYang: boolean }> = {
  甲:{hangul:"갑",yinYang:true}, 乙:{hangul:"을",yinYang:false},
  丙:{hangul:"병",yinYang:true}, 丁:{hangul:"정",yinYang:false},
  戊:{hangul:"무",yinYang:true}, 己:{hangul:"기",yinYang:false},
  庚:{hangul:"경",yinYang:true}, 辛:{hangul:"신",yinYang:false},
  壬:{hangul:"임",yinYang:true}, 癸:{hangul:"계",yinYang:false},
};
const BRANCH_INFO: Record<string, { hangul:string; ohaeng:string; yinYang:boolean; animalEmoji:string; animalName:string; jijanggan:string[] }> = {
  子:{hangul:"자",ohaeng:"수",yinYang:true, animalEmoji:"🐭",animalName:"쥐",    jijanggan:["壬","癸"]},
  丑:{hangul:"축",ohaeng:"토",yinYang:false,animalEmoji:"🐮",animalName:"소",    jijanggan:["癸","辛","己"]},
  寅:{hangul:"인",ohaeng:"목",yinYang:true, animalEmoji:"🐯",animalName:"호랑이",jijanggan:["戊","丙","甲"]},
  卯:{hangul:"묘",ohaeng:"목",yinYang:false,animalEmoji:"🐰",animalName:"토끼",  jijanggan:["甲","乙"]},
  辰:{hangul:"진",ohaeng:"토",yinYang:true, animalEmoji:"🐲",animalName:"용",    jijanggan:["乙","癸","戊"]},
  巳:{hangul:"사",ohaeng:"화",yinYang:false,animalEmoji:"🐍",animalName:"뱀",    jijanggan:["戊","庚","丙"]},
  午:{hangul:"오",ohaeng:"화",yinYang:true, animalEmoji:"🐴",animalName:"말",    jijanggan:["丙","己","丁"]},
  未:{hangul:"미",ohaeng:"토",yinYang:false,animalEmoji:"🐑",animalName:"양",    jijanggan:["丁","乙","己"]},
  申:{hangul:"신",ohaeng:"금",yinYang:true, animalEmoji:"🐵",animalName:"원숭이",jijanggan:["戊","壬","庚"]},
  酉:{hangul:"유",ohaeng:"금",yinYang:false,animalEmoji:"🐓",animalName:"닭",    jijanggan:["庚","辛"]},
  戌:{hangul:"술",ohaeng:"토",yinYang:true, animalEmoji:"🐕",animalName:"개",    jijanggan:["辛","丁","戊"]},
  亥:{hangul:"해",ohaeng:"수",yinYang:false,animalEmoji:"🐷",animalName:"돼지",  jijanggan:["戊","甲","壬"]},
};
const STEM_COLOR_KR: Record<string,string> = {
  甲:"청",乙:"청",丙:"붉은",丁:"붉은",戊:"황",己:"황",庚:"흰",辛:"흰",壬:"흑",癸:"흑",
};
const TWELVE_FORTUNE: Record<string, Record<string,string>> = {
  甲:{亥:"장생",子:"목욕",丑:"관대",寅:"건록",卯:"제왕",辰:"쇠",巳:"병",午:"사",未:"묘",申:"절",酉:"태",戌:"양"},
  乙:{午:"장생",巳:"목욕",辰:"관대",卯:"건록",寅:"제왕",丑:"쇠",子:"병",亥:"사",戌:"묘",酉:"절",申:"태",未:"양"},
  丙:{寅:"장생",卯:"목욕",辰:"관대",巳:"건록",午:"제왕",未:"쇠",申:"병",酉:"사",戌:"묘",亥:"절",子:"태",丑:"양"},
  丁:{酉:"장생",申:"목욕",未:"관대",午:"건록",巳:"제왕",辰:"쇠",卯:"병",寅:"사",丑:"묘",子:"절",亥:"태",戌:"양"},
  戊:{寅:"장생",卯:"목욕",辰:"관대",巳:"건록",午:"제왕",未:"쇠",申:"병",酉:"사",戌:"묘",亥:"절",子:"태",丑:"양"},
  己:{酉:"장생",申:"목욕",未:"관대",午:"건록",巳:"제왕",辰:"쇠",卯:"병",寅:"사",丑:"묘",子:"절",亥:"태",戌:"양"},
  庚:{巳:"장생",午:"목욕",未:"관대",申:"건록",酉:"제왕",戌:"쇠",亥:"병",子:"사",丑:"묘",寅:"절",卯:"태",辰:"양"},
  辛:{子:"장생",亥:"목욕",戌:"관대",酉:"건록",申:"제왕",未:"쇠",午:"병",巳:"사",辰:"묘",卯:"절",寅:"태",丑:"양"},
  壬:{申:"장생",酉:"목욕",戌:"관대",亥:"건록",子:"제왕",丑:"쇠",寅:"병",卯:"사",辰:"묘",巳:"절",午:"태",未:"양"},
  癸:{卯:"장생",寅:"목욕",丑:"관대",子:"건록",亥:"제왕",戌:"쇠",酉:"병",申:"사",未:"묘",午:"절",巳:"태",辰:"양"},
};
const TWELVE_SPIRIT: Record<string, Record<string,string>> = {
  "申子辰":{巳:"겁살",午:"재살",未:"천살",申:"지살",酉:"년살",戌:"월살",亥:"망신",子:"장성",丑:"반안",寅:"역마",卯:"육해",辰:"화개"},
  "亥卯未":{申:"겁살",酉:"재살",戌:"천살",亥:"지살",子:"년살",丑:"월살",寅:"망신",卯:"장성",辰:"반안",巳:"역마",午:"육해",未:"화개"},
  "寅午戌":{亥:"겁살",子:"재살",丑:"천살",寅:"지살",卯:"년살",辰:"월살",巳:"망신",午:"장성",未:"반안",申:"역마",酉:"육해",戌:"화개"},
  "巳酉丑":{寅:"겁살",卯:"재살",辰:"천살",巳:"지살",午:"년살",未:"월살",申:"망신",酉:"장성",戌:"반안",亥:"역마",子:"육해",丑:"화개"},
};

function getYearBranchGroup(yb: string): string {
  if (["申","子","辰"].includes(yb)) return "申子辰";
  if (["亥","卯","未"].includes(yb)) return "亥卯未";
  if (["寅","午","戌"].includes(yb)) return "寅午戌";
  return "巳酉丑";
}

const GAN_ORDER_GM = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const JI_ORDER_GM  = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const JI_KOR_MAP: Record<string,string> = {子:"자",丑:"축",寅:"인",卯:"묘",辰:"진",巳:"사",午:"오",未:"미",申:"신",酉:"유",戌:"술",亥:"해"};

function calcGongmang(dayGan: string, dayJi: string): string[] {
  const gi = GAN_ORDER_GM.indexOf(dayGan);
  const ji = JI_ORDER_GM.indexOf(dayJi);
  if (gi < 0 || ji < 0) return [];
  const start = (ji - gi + 12) % 12;
  return [JI_ORDER_GM[(start + 10) % 12], JI_ORDER_GM[(start + 11) % 12]];
}

// 60갑자 일진 계산 (기준: 1900-01-01 = 甲戌 = index 10)
const _60_STEMS   = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const _60_BRANCHES = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
function getDayGanzhi(year: number, month: number, day: number): { gan: string; ji: string } {
  const refMs = new Date(1900, 0, 1).getTime();
  const tgtMs = new Date(year, month - 1, day).getTime();
  const diff  = Math.round((tgtMs - refMs) / 86400000);
  const idx   = ((10 + diff) % 60 + 60) % 60;
  return { gan: _60_STEMS[idx % 10], ji: _60_BRANCHES[idx % 12] };
}

const CYCLE5 = ["목","화","토","금","수"];
function ohGen(a:string,b:string){return CYCLE5[(CYCLE5.indexOf(a)+1)%5]===b;}
function ohCtl(a:string,b:string){return CYCLE5[(CYCLE5.indexOf(a)+2)%5]===b;}

function calcTenStar(dayGan: string, targetChar: string): string {
  const dayOe = STEM_OHAENG[dayGan];
  const dayYY = STEM_INFO[dayGan]?.yinYang;
  if (!dayOe || dayYY === undefined) return "—";
  let tOe: string|undefined, tYY: boolean|undefined;
  if (STEM_INFO[targetChar])   { tOe=STEM_OHAENG[targetChar]; tYY=STEM_INFO[targetChar].yinYang; }
  else if (BRANCH_INFO[targetChar]) {
    const b=BRANCH_INFO[targetChar];
    tOe=b.ohaeng;
    const bonggi=b.jijanggan[b.jijanggan.length-1];
    tYY=STEM_INFO[bonggi]?.yinYang??b.yinYang;
  }
  if (!tOe||tYY===undefined) return "—";
  const same=dayYY===tYY;
  if (tOe===dayOe)          return same?"비견":"겁재";
  if (ohGen(dayOe,tOe))     return same?"식신":"상관";
  if (ohCtl(dayOe,tOe))     return same?"편재":"정재";
  if (ohGen(tOe,dayOe))     return same?"편인":"정인";
  if (ohCtl(tOe,dayOe))     return same?"편관":"정관";
  return "—";
}


const API = process.env.REACT_APP_API_URL || "";

/* ─── 시주 추정 설문 모달 styled ─────────────────────────────────────── */
const SurveyOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.55);
  z-index: 999; display: flex; align-items: center; justify-content: center;
  padding: 20px;
`;
const SurveyModal = styled.div`
  background: #fff; border-radius: 16px; width: 100%; max-width: 560px;
  max-height: 85vh; overflow-y: auto; padding: 28px 24px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.25);
`;
const SurveyTitle = styled.div`
  font-size: 16px; font-weight: 800; color: #1A1A2E; margin-bottom: 4px;
`;
const SurveySub = styled.div`
  font-size: 12px; color: #888; margin-bottom: 20px;
`;
const SurveyQBlock = styled.div`
  margin-bottom: 20px;
`;
const SurveyQText = styled.div`
  font-size: 13px; font-weight: 700; color: #333; margin-bottom: 8px;
`;
const SurveyChoiceList = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`;
const SurveyChoice = styled.label<{ $selected: boolean }>`
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 13px;
  background: ${p => p.$selected ? "#EEF2FF" : "#F8F9FA"};
  border: 1.5px solid ${p => p.$selected ? "#748FFC" : "#E9ECEF"};
  color: ${p => p.$selected ? "#3B5BDB" : "#444"};
  transition: all 0.15s;
  &:hover { background: #F0F4FF; border-color: #A5B4FC; }
`;
const SurveyResultCard = styled.div<{ $rank: number }>`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 12px; margin-bottom: 8px;
  background: ${p => p.$rank === 1 ? "#FFF9DB" : p.$rank === 2 ? "#F3F0FF" : "#F0F8FF"};
  border: 1.5px solid ${p => p.$rank === 1 ? "#F59F00" : p.$rank === 2 ? "#9775FA" : "#74C0FC"};
`;
const SurveyRankBadge = styled.div<{ $rank: number }>`
  width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center;
  justify-content: center; font-size: 13px; font-weight: 800; flex-shrink: 0;
  background: ${p => p.$rank === 1 ? "#F59F00" : p.$rank === 2 ? "#9775FA" : "#74C0FC"};
  color: #fff;
`;
const SurveyResultInfo = styled.div` flex: 1; `;
const SurveyAnalyzeBtn = styled.button`
  padding: 6px 14px; border-radius: 8px; border: none; cursor: pointer;
  background: #3B5BDB; color: #fff; font-size: 12px; font-weight: 700;
  white-space: nowrap;
  &:hover { background: #2f4cc2; }
`;

/* ─── 로그인 화면 styled ─────────────────────────────────────────────── */
const LoginWrap = styled.div`
  min-height:100vh;display:flex;align-items:center;justify-content:center;
  background:#f8f9fa;
`;
const LoginCard = styled.div`
  background:#fff;border:1px solid #e9ecef;border-radius:16px;
  padding:40px 32px;width:340px;box-shadow:0 4px 24px rgba(0,0,0,0.08);
  text-align:center;
`;
const LoginTitle = styled.h2`font-size:1.2rem;font-weight:800;color:#212529;margin-bottom:6px;`;
const LoginSub = styled.p`font-size:.82rem;color:#868e96;margin-bottom:28px;`;
const LoginInput = styled.input`
  width:100%;padding:10px 14px;border:1px solid #ced4da;border-radius:8px;
  font-size:.9rem;color:#212529;margin-bottom:12px;outline:none;
  &:focus{border-color:#339af0;box-shadow:0 0 0 3px rgba(51,154,240,0.15);}
`;
const LoginBtn = styled.button`
  width:100%;padding:12px;background:#1971c2;color:#fff;border:none;
  border-radius:8px;font-size:.9rem;font-weight:700;cursor:pointer;
  &:hover{background:#1864ab;}
  &:disabled{opacity:.6;cursor:not-allowed;}
`;
const LoginErr = styled.p`font-size:.8rem;color:#e03131;margin-top:10px;`;

const TABS = [
  { key: "palja",      label: "사주팔자",    color: "#212529" },
  { key: "ohaeng",     label: "오행분석",    color: "#51CF66" },
  { key: "daeun",      label: "대운별운세",  color: "#FF922B" },
  { key: "report",     label: "리포트 구성", color: "#7048E8" },
  { key: "edit_report",label: "✏️ 리포트 수정", color: "#e67700" },
];

const SCORE_LABELS: Record<string, string> = {
  love: "연애", marry: "결혼", business: "사업", career: "직업", health: "건강", wealth: "재물",
  family: "가족관계", friends: "친구관계",
};

const ALL_DAEUN_THEMES = [
  {key:"love",   label:"연애운"},
  {key:"marry",  label:"결혼운"},
  {key:"business",label:"사업운"},
  {key:"career", label:"직업운"},
  {key:"wealth", label:"재물운"},
  {key:"health", label:"건강운"},
  {key:"family", label:"가족관계"},
  {key:"friends",label:"친구관계"},
];

function getDefaultDaeunThemes(startAge: number): string[] {
  if (startAge < 10) return ["health","family","friends"];
  if (startAge < 20) return ["health","family","friends","career"];
  return ALL_DAEUN_THEMES.map(t => t.key);
}

type ClientEntry = {
  consultation_id: number; client_id: number; name: string; phone: string; email: string;
  gender: string; birth: string; status: string; created_at: string;
  service_type?: string; partner_name?: string;
  source?: string; email_sent?: boolean; report_view_count?: number;
};
type Consultation = {
  id: number; client_id: number; name: string; phone: string; email: string; gender: string;
  birth_year: number; birth_month: number; birth_day: number; birth_hour: number; birth_minute?: number;
  city?: string;
  status: string; ai_scores: any; saju: any; counselor_notes: string;
  service_type?: string; service_options?: string[]; partner_info?: any;
  report_config?: any; report_expires_at?: string;
  source?: string; email_sent?: boolean; email_sent_at?: string;
  report_view_count?: number; report_viewed_at?: string;
};

const DEFAULT_COMPAT_ITEMS: Record<string, boolean> = {
  "성격궁합": true, "속궁합": true, "주의점": true,
  "결혼운/가치관": false, "자녀운": false, "이별수/위험시기": false,
};

export default function AdminPage() {
  /* ─── 로그인 상태 ── */
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!sessionStorage.getItem("admin_token"));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loginId,  setLoginId]  = useState("");
  const [loginPw,  setLoginPw]  = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  /* ─── axios 인터셉터: 모든 요청에 토큰 자동 첨부 ── */
  React.useEffect(() => {
    const id = axios.interceptors.request.use(cfg => {
      const t = sessionStorage.getItem("admin_token");
      if (t) cfg.headers = { ...cfg.headers as any, Authorization: `Bearer ${t}` } as any;
      return cfg;
    });
    const rid = axios.interceptors.response.use(
      r => r,
      err => {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("admin_token");
          setIsLoggedIn(false);
        }
        return Promise.reject(err);
      }
    );
    return () => { axios.interceptors.request.eject(id); axios.interceptors.response.eject(rid); };
  }, []);

  const handleLogin = async () => {
    setLoginBusy(true); setLoginErr("");
    try {
      const r = await axios.post(`${API}/api/v1/auth/login`, { admin_id: loginId, password: loginPw });
      sessionStorage.setItem("admin_token", r.data.access_token);
      setIsLoggedIn(true);
      loadList();
    } catch (e: any) {
      setLoginErr(e.response?.data?.detail || "로그인 실패");
    } finally { setLoginBusy(false); }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/api/v1/auth/logout`).catch(() => {});
    sessionStorage.removeItem("admin_token");
    setIsLoggedIn(false);
  };

  const [list,    setList]    = useState<ClientEntry[]>([]);
  const [selId,   setSelId]   = useState<number | null>(null);
  const [consult, setConsult] = useState<Consultation | null>(null);
  const [tab,          setTab]          = useState("palja");
  const [busy,         setBusy]         = useState(false);
  const [msg,          setMsg]          = useState("");
  const [reportMd,     setReportMd]     = useState("");
  const [reportMdBusy, setReportMdBusy] = useState(false);
  const [isCustomMd,   setIsCustomMd]   = useState(false);
  const [applyHap,    setApplyHap]    = useState(false);
  const [applyWeight, setApplyWeight] = useState(false);
  const [applyHapP,    setApplyHapP]    = useState(false);
  const [applyWeightP, setApplyWeightP] = useState(false);
  const [applyHapCmp,    setApplyHapCmp]    = useState(false);
  const [applyWeightCmp, setApplyWeightCmp] = useState(false);
  /* ─── 리포트 구성 state ─────── */
  const [daeunEnabled,  setDaeunEnabled]  = useState(true);
  const [saeunEnabled,  setSaeunEnabled]  = useState(true);
  const [wolunEnabled,  setWolunEnabled]  = useState(false);
  const [daeunSelected,    setDaeunSelected]    = useState<string[]>([]);
  const [daeunThemeConfig, setDaeunThemeConfig] = useState<Record<string, string[]>>({});
  const [saeunSelected,    setSaeunSelected]    = useState<number[]>([]);
  const [wolunSelected,    setWolunSelected]    = useState<number[]>([]);
  const [compatItems,    setCompatItems]    = useState<Record<string,boolean>>(DEFAULT_COMPAT_ITEMS);
  const [compatAnalysis, setCompatAnalysis] = useState<Record<string,{score:string;text:string}>>({});
  const [sepYears,       setSepYears]       = useState<{year:string;months:string;reason:string}[]>([]);
  const [childrenScore,  setChildrenScore]  = useState("");
  const [marriageTiming, setMarriageTiming] = useState("");
  const [meetingTime,    setMeetingTime]    = useState("");
  const [idealType1,     setIdealType1]     = useState("");
  const [idealType2,     setIdealType2]     = useState("");
  const [romanceStyle1,  setRomanceStyle1]  = useState("");
  const [romanceStyle2,  setRomanceStyle2]  = useState("");
  const [childrenGender, setChildrenGender] = useState("");
  const [childrenTiming, setChildrenTiming] = useState("");
  const [partnerSaju,    setPartnerSaju]    = useState<any>(null);
  const [openSaeun,      setOpenSaeun]      = useState<Record<string|number,boolean>>({});
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [adminDaeunIdx,  setAdminDaeunIdx]  = useState<number | null>(null);
  const [adminSaeunYear, setAdminSaeunYear] = useState<number | null>(null);
  const [adminMonthKey,  setAdminMonthKey]  = useState<string | null>(null); // "${saeunYear}-${saju월}"
  const [selectedIds,    setSelectedIds]    = useState<Set<number>>(new Set());
  /* ─── 시주 추정 설문 state ─── */
  const [surveyOpen,    setSurveyOpen]    = useState(false);
  const [surveyQs,      setSurveyQs]      = useState<{id:number;question:string;choices:string[]}[]>([]);
  const [surveyAns,     setSurveyAns]     = useState<number[]>(Array(10).fill(-1));
  const [surveyResults, setSurveyResults] = useState<any[]>([]);
  const [todayFortune,  setTodayFortune]  = useState<any>(null);

  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`선택한 ${selectedIds.size}건을 삭제하시겠습니까?\n삭제된 항목은 Google Sheets에 취소줄로 표기됩니다.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/v1/admin/consultations/bulk-delete`, {
        consultation_ids: Array.from(selectedIds),
      });
      if (selId && selectedIds.has(selId)) { setSelId(null); setConsult(null); }
      setSelectedIds(new Set());
      await loadList();
      setMsg("삭제 완료");
    } catch { setMsg("삭제 실패"); }
    finally { setBusy(false); }
  };

  const loadList = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/v1/admin/waiting`); setList(r.data); } catch {}
  }, []);

  const openSurvey = async () => {
    if (surveyQs.length === 0) {
      try {
        const r = await axios.get(`${API}/api/v1/saju/hour-survey`);
        setSurveyQs(r.data.questions);
      } catch { setMsg("설문 불러오기 실패"); return; }
    }
    setSurveyAns(Array(10).fill(-1));
    setSurveyResults([]);
    setSurveyOpen(true);
  };

  const submitSurvey = async () => {
    if (surveyAns.some(a => a < 0)) {
      setMsg("모든 질문에 답해 주세요");
      return;
    }
    try {
      const r = await axios.post(`${API}/api/v1/saju/estimate-hour`, { answers: surveyAns });
      setSurveyResults(r.data.recommendations);
    } catch { setMsg("추정 실패"); }
  };

  const analyzeWithHour = async (birthHour: number) => {
    if (!selId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/v1/admin/analyze/${selId}?hour_override=${birthHour}`);
      await loadConsult(selId);
      setSurveyOpen(false);
      setMsg(`${birthHour}시로 분석 완료`);
    } catch (e: any) { setMsg("분석 실패: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const loadConsult = useCallback(async (id: number) => {
    let r: any;
    try {
      r = await axios.get(`${API}/api/v1/admin/consultation/${id}`);
    } catch (e: any) {
      setMsg("상담 정보 로드 실패: " + (e.response?.data?.detail || e.message || "서버 오류"));
      return;
    }
    setConsult(r.data);
    const rc = r.data.report_config;
    if (rc) {
      setDaeunEnabled(rc.daeun_enabled ?? true);
      setSaeunEnabled(rc.saeun_enabled ?? true);
      setWolunEnabled(rc.wolun_enabled ?? false);
      setDaeunSelected(rc.daeun_selected ?? []);
      setDaeunThemeConfig(rc.daeun_theme_config ?? {});
      setSaeunSelected(rc.saeun_selected ?? []);
      setWolunSelected(rc.wolun_selected ?? []);
      setCompatItems(
        (rc.compatibility_items && Object.keys(rc.compatibility_items).length > 0)
          ? rc.compatibility_items
          : DEFAULT_COMPAT_ITEMS
      );
      const rawAnalysis = rc.compat_analysis || {};
      const analysisState: Record<string,{score:string;text:string}> = {};
      for (const k of Object.keys(DEFAULT_COMPAT_ITEMS)) {
        analysisState[k] = { score: String(rawAnalysis[k]?.score ?? ""), text: rawAnalysis[k]?.text ?? "" };
      }
      setCompatAnalysis(analysisState);
      setSepYears(rc.separation_years || []);
      setChildrenScore(rc.children_score != null ? String(rc.children_score) : "");
      setMarriageTiming(rc.marriage_timing || "");
      setMeetingTime(rc.meeting_time || "");
      setIdealType1(rc.ideal_type_1 || "");
      setIdealType2(rc.ideal_type_2 || "");
      setRomanceStyle1(rc.romance_style_1 || "");
      setRomanceStyle2(rc.romance_style_2 || "");
      setChildrenGender(rc.children_gender || "");
      setChildrenTiming(rc.children_timing || "");
    } else {
      setDaeunEnabled(true); setSaeunEnabled(true); setWolunEnabled(false);
      setDaeunSelected([]); setDaeunThemeConfig({}); setSaeunSelected([]); setWolunSelected([]);
      setCompatItems(DEFAULT_COMPAT_ITEMS);
      const blank: Record<string,{score:string;text:string}> = {};
      for (const k of Object.keys(DEFAULT_COMPAT_ITEMS)) blank[k] = {score:"",text:""};
      setCompatAnalysis(blank);
      setSepYears([]); setChildrenScore(""); setMarriageTiming("");
      setMeetingTime(""); setIdealType1(""); setIdealType2("");
      setRomanceStyle1(""); setRomanceStyle2("");
      setChildrenGender(""); setChildrenTiming("");
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const loadTodayFortune = useCallback(async () => {
    if (!selId || !consult?.saju) return;
    axios.get(`${API}/api/v1/admin/today-fortune/${selId}`)
      .then(r => setTodayFortune(r.data))
      .catch(() => setTodayFortune(null));
  }, [selId, consult?.saju]);

  const select = (id: number) => { setSelId(id); setMsg(""); setTab("palja"); setPartnerSaju(null); setAdminDaeunIdx(null); setAdminSaeunYear(null); setTodayFortune(null); loadConsult(id); };

  const runAnalysis = async () => {
    if (!selId) return; setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/api/v1/admin/analyze/${selId}`);
      await loadConsult(selId); await loadList();
      setMsg("분석 완료!"); setTab("daeun");
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const saveReportConfig = async () => {
    if (!selId) return; setBusy(true);
    try {
      const svc = consult?.service_type || "종합운세";
      const isCompat = consult?.partner_info != null;
      const compatAnalysisPayload: Record<string,{score:number;text:string}> = {};
      for (const [k, v] of Object.entries(compatAnalysis)) {
        compatAnalysisPayload[k] = { score: parseInt(v.score) || 0, text: v.text };
      }
      await axios.put(`${API}/api/v1/admin/report-config/${selId}`, {
        daeun_enabled: daeunEnabled,
        saeun_enabled: saeunEnabled,
        wolun_enabled: wolunEnabled,
        daeun_selected: daeunSelected,
        daeun_theme_config: daeunThemeConfig,
        saeun_selected: saeunSelected,
        wolun_selected: wolunSelected,
        compatibility_type: isCompat ? (svc === "심층궁합" ? "deep" : "casual") : null,
        compatibility_items: compatItems,
        compat_analysis: compatAnalysisPayload,
        separation_years: sepYears,
        children_score: childrenScore ? parseInt(childrenScore) : null,
        marriage_timing: marriageTiming,
        meeting_time: meetingTime,
        ideal_type_1: idealType1,
        ideal_type_2: idealType2,
        romance_style_1: romanceStyle1,
        romance_style_2: romanceStyle2,
        children_gender: childrenGender,
        children_timing: childrenTiming,
      });
      setMsg("리포트 구성 저장 완료");
    } catch { setMsg("저장 실패"); } finally { setBusy(false); }
  };

  const loadPartnerSaju = async () => {
    if (!selId) return;
    setPartnerLoading(true);
    try {
      const r = await axios.post(`${API}/api/v1/admin/analyze-partner/${selId}`);
      setPartnerSaju(r.data);
    } catch (e: any) {
      setMsg("상대방 사주 분석 실패: " + (e.response?.data?.detail || e.message));
    } finally { setPartnerLoading(false); }
  };

  const calcCompatScore = async () => {
    if (!selId) return; setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/api/v1/admin/compat-score/${selId}`);
      await loadConsult(selId);
      setMsg("궁합 점수 계산 완료!");
    } catch (e: any) {
      setMsg("오류: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!selId || !consult) return;
    const isVisit = consult.source === "visit";
    const email = consult.email;
    // 이메일 자동발송이 일어나는 경우(비방문+이메일 있음)만 확인
    if (!isVisit && email) {
      if (!window.confirm(`[이메일 발송 확인]\n\n수신: ${consult.name} (${email})\n\n이 고객에게 사주 리포트를 이메일로 발송합니다.\n계속하시겠습니까?`)) return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/v1/admin/complete/${selId}`);
      await loadList(); await loadConsult(selId);
      setMsg(r.data.is_visit ? "상담 완료 처리됨 (이메일 미발송 — 아래 전송 버튼으로 발송하세요)" : "상담 완료 처리됨");
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const sendEmail = async () => {
    if (!selId || !consult) return;
    if (!window.confirm(`[이메일 발송 확인]\n\n수신: ${consult.name} (${consult.email})\n\n이 고객에게 사주 리포트를 이메일로 발송합니다.\n계속하시겠습니까?`)) return;
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/api/v1/admin/send-email/${selId}`);
      await loadList(); await loadConsult(selId);
      setMsg(r.data.email_sent ? `이메일 발송 완료 → ${r.data.to_email}` : "이메일 발송 실패 (설정 확인 필요)");
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const previewEmail = async () => {
    if (!consult?.id) return;
    try {
      const r = await axios.get(`${API}/api/v1/admin/email-preview/${consult.id}`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) { w.document.write(r.data); w.document.close(); }
    } catch (e: any) { setMsg("미리보기 실패: " + (e.response?.data?.detail || e.message)); }
  };

  const resetReport = async () => {
    if (!selId) return;
    if (!window.confirm(`[이메일 재발송 확인]\n\n수신: ${consult?.name} (${consult?.email})\n\n리포트 만료 시간을 24시간 연장하고 이메일을 재발송합니다.\n계속하시겠습니까?`)) return;
    setBusy(true);
    setMsg("리셋 중...");
    try {
      const r = await axios.post(`${API}/api/v1/admin/reset-report/${selId}`);
      const exp = new Date(r.data.report_expires_at).toLocaleString("ko-KR");
      setMsg(`리셋 완료 — 만료: ${exp}${r.data.email_sent ? " | 이메일 재발송됨" : " | 이메일 미발송(설정 확인 필요)"}`);
      try { await loadList(); await loadConsult(selId); } catch {}
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const regenerateReport = async () => {
    if (!selId || !consult) return;
    if (!window.confirm(`[리포트 재생성]\n\n${consult.name}님의 리포트를 최신 형식으로 다시 생성합니다.\n기존 리포트 내용이 새 형식으로 교체됩니다.\n계속하시겠습니까?`)) return;
    setBusy(true); setMsg("재생성 중...");
    try {
      await axios.post(`${API}/api/v1/admin/regenerate-report/${selId}`);
      await loadConsult(selId);
      setMsg("✅ 리포트가 최신 형식으로 재생성되었습니다.");
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const batchRegenerate = async () => {
    if (!window.confirm("전체 상담 리포트를 최신 로직으로 일괄 재생성합니다.\n건수에 따라 수십 초 이상 걸릴 수 있습니다.\n계속하시겠습니까?")) return;
    setBusy(true); setMsg("일괄 재생성 중... (완료까지 기다려주세요)");
    try {
      const r = await axios.post(`${API}/api/v1/admin/batch-regenerate`);
      const d = r.data;
      setMsg(`✅ 일괄 재생성 완료 — 성공 ${d.success}건 / 실패 ${d.failed}건 / 건너뜀 ${d.skipped}건`);
      await loadList();
    } catch (e: any) { setMsg("오류: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  /* 데이터 파싱 */
  const scores      = consult?.ai_scores;
  const detail      = scores?.detail       || {};
  const hapChung    = detail?.hap_chung    || consult?.saju?.hap_chung || {};
  const daeunScores: any[] = scores?.daeun_scores || [];
  const loveProfile   = scores?.love_profile   || null;
  const wealthProfile = scores?.wealth_profile || null;

  const palja = consult?.saju?.saju_palja;
  const wuxing = consult?.saju?.wuxing_analysis;

  const hasHapChung = Object.values(hapChung).some((v: any) => v?.length > 0);

  /* ─── 로그인 화면 (모든 hooks 이후에 분기) ── */
  if (!isLoggedIn) {
    return (
      <>
        <GlobalStyle />
        <LoginWrap>
          <LoginCard>
            <LoginTitle>🔐 관리자 로그인</LoginTitle>
            <LoginSub>우연 사주 관리자 페이지</LoginSub>
            <LoginInput
              type="text" placeholder="관리자 ID" value={loginId}
              onChange={e => setLoginId(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
            />
            <LoginInput
              type="password" placeholder="비밀번호" value={loginPw}
              onChange={e => setLoginPw(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
            />
            <LoginBtn onClick={handleLogin} disabled={loginBusy}>
              {loginBusy ? "로그인 중..." : "로그인"}
            </LoginBtn>
            {loginErr && <LoginErr>{loginErr}</LoginErr>}
          </LoginCard>
        </LoginWrap>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      {/* ── 시주 추정 설문 모달 ─────────────────────────────────────────── */}
      {surveyOpen && (
        <SurveyOverlay onClick={e => { if (e.target === e.currentTarget) setSurveyOpen(false); }}>
          <SurveyModal>
            <SurveyTitle>출생시간(시주) 추정 설문</SurveyTitle>
            <SurveySub>10개 질문에 답하면 가능성 높은 시주 3개를 추천합니다.</SurveySub>

            {surveyResults.length === 0 ? (
              <>
                {surveyQs.map((q, qi) => (
                  <SurveyQBlock key={q.id}>
                    <SurveyQText>{qi + 1}. {q.question}</SurveyQText>
                    <SurveyChoiceList>
                      {q.choices.map((c, ci) => (
                        <SurveyChoice key={ci} $selected={surveyAns[qi] === ci}
                          onClick={() => setSurveyAns(a => { const n=[...a]; n[qi]=ci; return n; })}>
                          <input type="radio" readOnly checked={surveyAns[qi] === ci}
                            style={{marginTop:2,accentColor:"#748FFC"}} />
                          {c}
                        </SurveyChoice>
                      ))}
                    </SurveyChoiceList>
                  </SurveyQBlock>
                ))}
                <div style={{display:"flex",gap:10,marginTop:8}}>
                  <button onClick={() => setSurveyOpen(false)}
                    style={{flex:1,padding:"10px",border:"1px solid #dee2e6",borderRadius:8,background:"#f8f9fa",cursor:"pointer",fontSize:13}}>
                    취소
                  </button>
                  <button onClick={submitSurvey}
                    style={{flex:2,padding:"10px",border:"none",borderRadius:8,background:"#3B5BDB",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>
                    시주 추정하기
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:13,fontWeight:700,color:"#333",marginBottom:12}}>추정 결과 (가능성 높은 순)</div>
                {surveyResults.map((r) => (
                  <SurveyResultCard key={r.rank} $rank={r.rank}>
                    <SurveyRankBadge $rank={r.rank}>{r.rank}</SurveyRankBadge>
                    <SurveyResultInfo>
                      <div style={{fontWeight:800,fontSize:14,color:"#1a1a2e"}}>
                        {r.name} ({r.hanja}) — {r.time_range}
                      </div>
                      <div style={{fontSize:12,color:"#888",marginTop:2}}>
                        오행: {r.ohaeng} · {r.animal}띠 · 일치도 {r.confidence}%
                      </div>
                    </SurveyResultInfo>
                    <SurveyAnalyzeBtn onClick={() => analyzeWithHour(r.birth_hour)}>
                      이 시주로 분석
                    </SurveyAnalyzeBtn>
                  </SurveyResultCard>
                ))}
                <div style={{display:"flex",gap:10,marginTop:12}}>
                  <button onClick={() => setSurveyResults([])}
                    style={{flex:1,padding:"9px",border:"1px solid #dee2e6",borderRadius:8,background:"#f8f9fa",cursor:"pointer",fontSize:13}}>
                    다시 풀기
                  </button>
                  <button onClick={() => setSurveyOpen(false)}
                    style={{flex:1,padding:"9px",border:"1px solid #dee2e6",borderRadius:8,background:"#f8f9fa",cursor:"pointer",fontSize:13}}>
                    닫기
                  </button>
                </div>
              </>
            )}
          </SurveyModal>
        </SurveyOverlay>
      )}
      <Layout>
        {/* ── 사이드바 ──────────────────────────────────────── */}
        <SidebarWrap $open={sidebarOpen}>
          <Sidebar>
            <SideHeader>
              <SideTitle>상담 대기 목록</SideTitle>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <RefreshBtn onClick={loadList}>새로고침</RefreshBtn>
                {selectedIds.size > 0 && (
                  <RefreshBtn
                    onClick={handleBulkDelete}
                    style={{color:"#ff6b6b", background:"rgba(255,50,50,0.15)", border:"1px solid rgba(255,80,80,0.3)"}}
                  >
                    삭제 ({selectedIds.size})
                  </RefreshBtn>
                )}
                <RefreshBtn onClick={batchRegenerate} disabled={busy} style={{color:"#5B9BD5", background:"rgba(91,155,213,0.15)", border:"1px solid rgba(91,155,213,0.3)"}}>전체 재생성</RefreshBtn>
                <RefreshBtn onClick={handleLogout} style={{color:"#ff8787"}}>로그아웃</RefreshBtn>
              </div>
            </SideHeader>
            <ClientList>
              {list.length === 0 && (
                <div style={{ padding: "20px 16px", color: "rgba(255,255,255,0.3)", fontSize: 13 }}>
                  대기 내담자 없음
                </div>
              )}
              {list.map(c => (
                <ClientItem key={c.consultation_id} $active={selId === c.consultation_id} $status={c.status}
                  onClick={() => select(c.consultation_id)}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:8}}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.consultation_id)}
                      onChange={() => {}}
                      onClick={(e) => toggleSelect(c.consultation_id, e)}
                      style={{marginTop:3,cursor:"pointer",accentColor:"#ff6b6b",flexShrink:0}}
                    />
                    <div style={{flex:1,minWidth:0}}>
                      <ClientName>
                        {c.name}
                        <StatusBadge $status={c.status}>
                          {c.status === "completed" ? "완료" : c.status === "in_progress" ? "분석됨" : "대기"}
                        </StatusBadge>
                        {c.source === "visit" && (
                          <span style={{ marginLeft: 4, padding: "1px 6px", background: "rgba(107,76,53,0.35)", borderRadius: 8, fontSize: 10, fontWeight: 700, color: "#e8c99a" }}>방문</span>
                        )}
                      </ClientName>
                      <ClientMeta>{c.birth} · {c.gender === "female" ? "여" : "남"}</ClientMeta>
                      <ClientMeta style={{ color: "rgba(255,255,255,0.55)" }}>
                        {c.partner_name
                          ? `${c.service_type} · ${c.name} ↔ ${c.partner_name}`
                          : `${c.service_type || "종합운세"}`}
                        {c.email ? ` · ${c.email}` : ""}
                      </ClientMeta>
                      <ClientMeta style={{ color: "rgba(255,255,255,0.45)", fontSize: ".68rem", marginTop: 2 }}>
                        {c.email_sent ? "📧 이메일 발송완료" : c.email ? "📧 미발송" : ""}
                        {(c.report_view_count ?? 0) > 0
                          ? `${c.email_sent ? " · " : ""}👁 ${c.report_view_count}회 열람`
                          : c.status === "completed" ? `${c.email_sent ? " · " : ""}👁 미열람` : ""}
                      </ClientMeta>
                    </div>
                  </div>
                </ClientItem>
              ))}
            </ClientList>
          </Sidebar>
        </SidebarWrap>
        {/* CollapseBtn: Layout 기준 absolute — SidebarWrap 밖에 독립 배치 */}
        <CollapseBtn $open={sidebarOpen} onClick={() => setSidebarOpen(v => !v)} title={sidebarOpen ? "목록 접기" : "목록 펼치기"}>
          {sidebarOpen ? "◀" : "▶"}
        </CollapseBtn>

        {/* ── 메인 ───────────────────────────────────────────── */}
        <Main>
          {!consult ? (
            <EmptyState>
              <span style={{ fontSize: 32 }}>☰</span>
              <span>왼쪽에서 내담자를 선택하세요</span>
            </EmptyState>
          ) : (
            <>
              {/* 프로필 헤더 */}
              <ProfileHeader>
                <ProfileName>
                  {consult.name}
                  <span style={{ fontSize: 13, fontWeight: 400, marginLeft: 10, opacity: 0.6 }}>
                    {consult.gender === "female" ? "여성" : "남성"}
                  </span>
                  {consult.source === "visit" ? (
                    <span style={{ marginLeft: 8, padding: "2px 8px", background: "#6b4c3520", border: "1.5px solid #6b4c3550", borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#6b4c35" }}>🏠 방문</span>
                  ) : (
                    <span style={{ marginLeft: 8, padding: "2px 8px", background: "#1971C220", border: "1.5px solid #1971C250", borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#1971C2" }}>📱 온라인</span>
                  )}
                </ProfileName>
                <ProfileMeta>
                  {consult.birth_year}.{String(consult.birth_month).padStart(2,"0")}.{String(consult.birth_day).padStart(2,"0")} {fmtHour(consult.birth_hour, consult.birth_minute)}{consult.city ? ` · ${consult.city}` : ""}{consult.email ? ` · ${consult.email}` : ""}
                </ProfileMeta>
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={loadTodayFortune}
                    style={{ fontSize: 11, padding: "2px 10px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#ADB5BD", cursor: "pointer" }}
                  >오늘 운세 확인</button>
                </div>
                {todayFortune && (
                  <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#868E96", marginRight: 2 }}>오늘 운세</span>
                    {([
                      { label: "세운", f: todayFortune.saeun },
                      { label: "월운", f: todayFortune.wolun },
                      { label: "일운", f: todayFortune.ilun },
                    ] as { label: string; f: any }[]).map(({ label, f }) => (
                      <div key={label} style={{
                        display: "flex", alignItems: "center", gap: 5,
                        background: "rgba(255,255,255,0.05)",
                        border: `1px solid ${f.color}50`,
                        borderRadius: 10, padding: "3px 10px", fontSize: 12,
                      }}>
                        <span style={{ color: "#868E96", fontSize: 11 }}>{label}</span>
                        <span style={{ color: "#CBD5E0", fontWeight: 600 }}>{f.ganzhi}</span>
                        <span style={{
                          padding: "1px 6px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                          background: f.color + "22", color: f.color,
                        }}>{f.level}</span>
                        <span style={{ color: f.color, fontWeight: 700, fontSize: 13 }}>{f.score}</span>
                      </div>
                    ))}
                    {todayFortune.use_god && (
                      <span style={{ fontSize: 11, color: "#74C0FC", marginLeft: 4 }}>
                        용신 {todayFortune.use_god} · 기신 {todayFortune.avoid_god}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Btn $variant="primary" onClick={runAnalysis} disabled={busy}
                    style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)" }}>
                    {busy ? "분석 중..." : "사주 엔진 분석"}
                  </Btn>
                  {consult.partner_info && (
                    <Btn onClick={calcCompatScore} disabled={busy || !scores || !consult.partner_info}
                      style={{ background: "rgba(230,73,128,0.25)", color: "#f472b6", border: "1px solid rgba(230,73,128,0.4)" }}>
                      {busy ? "계산 중..." : "궁합 점수 계산"}
                    </Btn>
                  )}
                  {consult.saju && (
                    <Btn onClick={previewEmail}
                      style={{ background: "rgba(112,72,168,0.25)", color: "#c9a8f0", border: "1px solid rgba(112,72,168,0.4)" }}>
                      👁 이메일 미리보기
                    </Btn>
                  )}
                  <Btn onClick={complete} disabled={busy || !scores || consult.status === "completed"}
                    style={{ background: "#2F9E44", color: "#fff", border: "none" }}>
                    {busy ? "처리 중..." : "상담 완료"}
                  </Btn>
                  {consult.status === "completed" && (
                    <Btn onClick={() => window.open(`/report/${consult.id}`, "_blank")}
                      style={{ background: "rgba(255,255,255,0.12)", color: "#A5D8FF", border: "1px solid rgba(255,255,255,0.18)" }}>
                      결과 리포트 보기
                    </Btn>
                  )}
                  {consult.status === "completed" && (
                    <Btn
                      onClick={() => {
                        const url = `${window.location.origin}/report/${consult.id}`;
                        navigator.clipboard.writeText(url).then(() => {
                          setMsg(`링크 복사 완료 → ${url}`);
                          setTimeout(() => setMsg(""), 4000);
                        });
                      }}
                      style={{ background: "#FFEB33", color: "#1a1a1a", border: "none", fontWeight: 700 }}>
                      📋 링크 복사
                    </Btn>
                  )}
                  {consult.status === "completed" && consult.email && consult.source === "visit" && (
                    <Btn onClick={sendEmail} disabled={busy}
                      style={{ background: "#FAE100", color: "#3C1E1E", border: "none", fontWeight: 700 }}>
                      {busy ? "발송 중..." : consult.email_sent ? "📧 이메일 재발송" : "📧 이메일 전송"}
                    </Btn>
                  )}
                  {consult.status === "completed" && (
                    <Btn onClick={resetReport} disabled={busy}
                      style={{ background: "rgba(255,100,100,0.2)", color: "#FF6B6B", border: "1px solid rgba(255,100,100,0.4)", fontWeight: 700 }}>
                      🔄 리포트 리셋
                    </Btn>
                  )}
                  {consult.status === "completed" && (
                    <Btn onClick={regenerateReport} disabled={busy}
                      style={{ background: "rgba(120,80,255,0.2)", color: "#9775FA", border: "1px solid rgba(120,80,255,0.4)", fontWeight: 700 }}>
                      ⚡ 리포트 재생성
                    </Btn>
                  )}
                </div>
                {msg && <div style={{ fontSize: 12, color: "#69DB7C", marginTop: 8 }}>{msg}</div>}
              </ProfileHeader>

              {/* 탭바 */}
              {(() => {
                const isCompatSvc = consult.partner_info != null;
                const activeTabs = isCompatSvc
                  ? [
                      ...TABS.slice(0,3),
                      { key: "partner", label: consult.partner_info?.name || "상대방", color: "#E64980" },
                      ...TABS.slice(3),
                    ]
                  : TABS;
                return (
                  <TabBar>
                    {activeTabs.map(t => (
                      <Tab key={t.key} $active={tab === t.key} $color={t.color}
                        onClick={async () => {
                          setTab(t.key);
                          if (t.key === "edit_report" && selId) {
                            setReportMdBusy(true);
                            try {
                              const r = await axios.get(`${API}/api/v1/admin/report-markdown/${selId}`);
                              setReportMd(r.data.markdown || "");
                              setIsCustomMd(r.data.is_custom || false);
                            } catch { setReportMd("(리포트 로드 실패 — 사주 분석을 먼저 실행하세요)"); }
                            finally { setReportMdBusy(false); }
                          }
                        }}>
                        {t.label}
                      </Tab>
                    ))}
                  </TabBar>
                );
              })()}

              {/* ── 탭: 사주팔자 ─────────────────────────────── */}
              {tab === "palja" && (
                <>
                  {/* 내담자 기본정보 */}
                  <ResultCard>
                    <CardTitle>내담자 정보</CardTitle>
                    <InfoGrid>
                      <InfoChip><InfoLabel>이름</InfoLabel><InfoValue>{consult.name}</InfoValue></InfoChip>
                      <InfoChip><InfoLabel>성별</InfoLabel><InfoValue>{consult.gender === "female" ? "여성" : "남성"}</InfoValue></InfoChip>
                      <InfoChip>
                        <InfoLabel>생년월일</InfoLabel>
                        <InfoValue>{consult.birth_year}.{String(consult.birth_month).padStart(2,"0")}.{String(consult.birth_day).padStart(2,"0")} {fmtHour(consult.birth_hour, consult.birth_minute)}</InfoValue>
                      </InfoChip>
                      <InfoChip><InfoLabel>출생지역</InfoLabel><InfoValue>{consult.city || "서울"}</InfoValue></InfoChip>
                      <InfoChip><InfoLabel>이메일</InfoLabel><InfoValue>{consult.email || "미입력"}</InfoValue></InfoChip>
                      <InfoChip>
                        <InfoLabel>서비스</InfoLabel>
                        <InfoValue style={{color:"#7048E8"}}>{consult.service_type || "종합운세"}</InfoValue>
                      </InfoChip>
                      <InfoChip>
                        <InfoLabel>상태</InfoLabel>
                        <InfoValue style={{color:consult.status==="completed"?"#2F9E44":consult.status==="in_progress"?"#FF922B":"var(--text-primary)"}}>
                          {consult.status==="completed"?"완료":consult.status==="in_progress"?"분석됨":"대기"}
                        </InfoValue>
                      </InfoChip>
                    </InfoGrid>
                    {/* 시간 모름 → 설문으로 시주 추정 버튼 */}
                    {(consult.birth_hour == null || consult.birth_hour === 99) && (
                      <div style={{marginTop:10,padding:"10px 14px",background:"#FFF3BF",border:"1px solid #F59F00",borderRadius:10,display:"flex",alignItems:"center",gap:12}}>
                        <div style={{flex:1,fontSize:12,color:"#7C4B00"}}>
                          생시(시간)가 입력되지 않았습니다. 설문으로 가능성 높은 시주를 추정할 수 있습니다.
                        </div>
                        <button onClick={openSurvey}
                          style={{padding:"7px 14px",border:"none",borderRadius:8,background:"#F59F00",color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                          설문으로 시주 추정
                        </button>
                      </div>
                    )}
                    {Array.isArray(consult.service_options) && consult.service_options.length > 0 && (
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                        <span style={{fontSize:11,color:"var(--text-secondary)",fontWeight:700,alignSelf:"center"}}>선택항목:</span>
                        {consult.service_options.map((opt:string) => (
                          <span key={opt} style={{fontSize:12,padding:"3px 10px",borderRadius:12,background:"#F0EBFF",color:"#7048E8",border:"1px solid #D0BFFF"}}>{opt}</span>
                        ))}
                      </div>
                    )}
                    {consult.partner_info && (
                      <div style={{marginTop:12,padding:"12px 14px",background:"#FFF0F6",border:"1px solid #FFBFD6",borderRadius:12}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#C2255C",marginBottom:8}}>
                          {consult.partner_info.compatibility_type === "deep" ? "💍 심층궁합" : "💕 연애궁합"} — 상대방 정보
                        </div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <InfoChip style={{background:"#fff4f8"}}><InfoLabel>이름</InfoLabel><InfoValue>{consult.partner_info.name}</InfoValue></InfoChip>
                          <InfoChip style={{background:"#fff4f8"}}><InfoLabel>성별</InfoLabel><InfoValue>{consult.partner_info.gender === "female" ? "여성" : "남성"}</InfoValue></InfoChip>
                          <InfoChip style={{background:"#fff4f8"}}>
                            <InfoLabel>생년월일</InfoLabel>
                            <InfoValue>{consult.partner_info.birth_year}.{String(consult.partner_info.birth_month||0).padStart(2,"0")}.{String(consult.partner_info.birth_day||0).padStart(2,"0")} {consult.partner_info.birth_hour || "?"}시</InfoValue>
                          </InfoChip>
                          <InfoChip style={{background:"#fff4f8"}}><InfoLabel>지역</InfoLabel><InfoValue>{consult.partner_info.city}</InfoValue></InfoChip>
                        </div>
                      </div>
                    )}
                  </ResultCard>

                  {/* 사주팔자 명식 테이블 */}
                  {palja && (() => {
                    const dayGan = palja.day_pillar?.stem || "";
                    const dayBranch = palja.day_pillar?.branch || "";
                    const yearBranch = palja.year_pillar?.branch || "";
                    const yearGroup = getYearBranchGroup(yearBranch);
                    const brInfo = BRANCH_INFO[dayBranch];
                    const colorKr = STEM_COLOR_KR[dayGan] || "";

                    // 전통 좌→우: 시주|일주|월주|년주
                    const _hStem = palja.hour_pillar?.stem||"";
                    const _hJi   = palja.hour_pillar?.branch||"";
                    const cols = [
                      { key:"시", gan:_hStem, ji:_hJi, isUnknown:!_hStem&&!_hJi, ganPk:"hour_gan", jiPk:"hour_ji" },
                      { key:"일", gan:palja.day_pillar?.stem||"",  ji:palja.day_pillar?.branch||"",  ganPk:"day_gan",  jiPk:"day_ji"  },
                      { key:"월", gan:palja.month_pillar?.stem||"",ji:palja.month_pillar?.branch||"",ganPk:"month_gan",jiPk:"month_ji"},
                      { key:"년", gan:palja.year_pillar?.stem||"", ji:palja.year_pillar?.branch||"", ganPk:"year_gan", jiPk:"year_ji" },
                    ];
                    const pp = detail?.sinsal?.per_pillar || {};
                    const labelTd: React.CSSProperties = {
                      width:38,textAlign:"center",padding:"4px 2px",
                      background:"#F0F0F0",border:"1px solid #E9ECEF",
                      fontSize:12,color:"#444444",fontWeight:700,verticalAlign:"middle",
                    };
                    const dataTd = (isDay:boolean): React.CSSProperties => ({
                      textAlign:"center",padding:"7px 4px",
                      background:isDay?"rgba(26,26,46,0.04)":"#fff",
                      border:"1px solid #E9ECEF",verticalAlign:"middle",color:"#222222",
                    });
                    const smallTd = (isDay:boolean): React.CSSProperties => ({
                      textAlign:"center",padding:"5px 4px",
                      background:isDay?"rgba(26,26,46,0.04)":"#FAFAFA",
                      border:"1px solid #E9ECEF",verticalAlign:"middle",color:"#222222",
                    });

                    return (
                      <ResultCard>
                        {/* 일주 설명 */}
                        <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:20,padding:"12px 14px",background:"#F8F9FA",borderRadius:12}}>
                          <div style={{fontSize:36,lineHeight:1,flexShrink:0}}>{brInfo?.animalEmoji||"☯"}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:18,fontWeight:800,color:"#222222",marginBottom:6}}>
                              {dayGan}{dayBranch} 일주
                              <span style={{fontWeight:600,color:"#222222",fontSize:15,marginLeft:6}}>
                                ({colorKr} {brInfo?.animalName||""})
                              </span>
                            </div>
                            <div style={{fontSize:15,color:"#222222",fontWeight:500}}>
                              {consult.birth_year}.{String(consult.birth_month).padStart(2,"0")}.{String(consult.birth_day).padStart(2,"0")} {fmtHour(consult.birth_hour, consult.birth_minute)} · {consult.gender==="female"?"여자":"남자"}{consult.city ? ` · ${consult.city}` : ""}
                            </div>
                          </div>
                        </div>

                        {/* 명식 테이블 */}
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed",minWidth:260}}>
                            <thead>
                              <tr>
                                <td style={{width:38,background:"#F8F9FA",border:"1px solid #E9ECEF",borderBottom:"2px solid #E9ECEF"}}/>
                                {cols.map((col,ci)=>(
                                  <td key={ci} style={{textAlign:"center",padding:"8px 4px",background:ci===1?"#1A1A2E":"#F0F0F0",color:ci===1?"#fff":"#444444",fontSize:12,fontWeight:700,border:"1px solid #E9ECEF",borderBottom:"2px solid #E9ECEF"}}>
                                    {col.key}주
                                  </td>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {/* 천간 */}
                              <tr>
                                <td style={labelTd}>천간</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={{...dataTd(ci===1),paddingBottom:20,paddingTop:8}}><div style={{fontSize:18,color:"#aaa",fontWeight:700}}>?</div></td>;
                                  const oe=STEM_OHAENG[col.gan]||"토";
                                  const st=OHAENG_STYLE[oe];
                                  const info=STEM_INFO[col.gan];
                                  const yy=info?.yinYang?"+":"−";
                                  return (
                                    <td key={ci} style={{...dataTd(ci===1),position:"relative",paddingBottom:20,paddingTop:8}}>
                                      <div style={{fontSize:15,color:st?.color||"#333",marginBottom:2,fontWeight:600}}>{info?.hangul}</div>
                                      <div style={{fontSize:28,fontWeight:800,lineHeight:1,color:st?.color||"#333"}}>{col.gan}</div>
                                      <div style={{position:"absolute",bottom:4,right:5,fontSize:15,fontWeight:700,color:st?.color||"#333"}}>{yy}{oe}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 십성(천간) */}
                              <tr>
                                <td style={labelTd}>십성</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={dataTd(ci===1)}><span style={{color:"#ccc"}}>—</span></td>;
                                  const star=calcTenStar(dayGan,col.gan);
                                  const oe=STEM_OHAENG[col.gan]||"토";
                                  return (
                                    <td key={ci} style={dataTd(ci===1)}>
                                      <span style={{fontSize:15,fontWeight:700,color:OHAENG_STYLE[oe]?.color||"#333"}}>{star}</span>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 신살·길성(천간) */}
                              <tr>
                                <td style={{...labelTd,fontSize:10}}>신살</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}><span style={{fontSize:11,color:"#ccc"}}>—</span></td>;
                                  const ganSinsal:string[] = pp[col.ganPk] || [];
                                  return (
                                    <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}>
                                      {ganSinsal.length>0 ? (
                                        <div style={{display:"flex",flexDirection:"column",gap:1,alignItems:"center"}}>
                                          {ganSinsal.map((s:string,si:number)=>{
                                            const isGil=["귀인","귀학","암록","복성","학당","금여","월덕","천덕"].some(w=>s.includes(w));
                                            return <span key={si} style={{fontSize:11,fontWeight:700,color:isGil?"#2F9E44":"#C92A2A",whiteSpace:"nowrap"}}>{s}</span>;
                                          })}
                                        </div>
                                      ) : <span style={{fontSize:11,color:"#ccc"}}>—</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 지지 */}
                              <tr>
                                <td style={labelTd}>지지</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={{...dataTd(ci===1),paddingBottom:20,paddingTop:8}}><div style={{fontSize:18,color:"#aaa",fontWeight:700}}>?</div></td>;
                                  const br=BRANCH_INFO[col.ji];
                                  const oe=br?.ohaeng||BRANCH_OHAENG[col.ji]||"토";
                                  const st=OHAENG_STYLE[oe];
                                  const bonggi=br?.jijanggan?.[br.jijanggan.length-1]??"";
                                  const yy=(STEM_INFO[bonggi]?.yinYang??br?.yinYang)?"+":"−";
                                  return (
                                    <td key={ci} style={{...dataTd(ci===1),position:"relative",paddingBottom:20,paddingTop:8}}>
                                      <div style={{fontSize:15,color:st?.color||"#333",marginBottom:2,fontWeight:600}}>{br?.hangul}</div>
                                      <div style={{fontSize:28,fontWeight:800,lineHeight:1,color:st?.color||"#333"}}>{col.ji}</div>
                                      <div style={{position:"absolute",bottom:4,right:5,fontSize:15,fontWeight:700,color:st?.color||"#333"}}>{yy}{oe}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 십성(지지) */}
                              <tr>
                                <td style={labelTd}>십성</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={dataTd(ci===1)}><span style={{color:"#ccc"}}>—</span></td>;
                                  const star=calcTenStar(dayGan,col.ji);
                                  const oe=BRANCH_INFO[col.ji]?.ohaeng||BRANCH_OHAENG[col.ji]||"토";
                                  return (
                                    <td key={ci} style={dataTd(ci===1)}>
                                      <span style={{fontSize:15,fontWeight:700,color:OHAENG_STYLE[oe]?.color||"#333"}}>{star}</span>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 신살·길성(지지) */}
                              <tr>
                                <td style={{...labelTd,fontSize:10}}>신살</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}><span style={{fontSize:11,color:"#ccc"}}>—</span></td>;
                                  const jiSinsal:string[] = pp[col.jiPk] || [];
                                  return (
                                    <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}>
                                      {jiSinsal.length>0 ? (
                                        <div style={{display:"flex",flexDirection:"column",gap:1,alignItems:"center"}}>
                                          {jiSinsal.map((s:string,si:number)=>{
                                            const isGil=["귀인","귀학","암록","복성","학당","금여","월덕","천덕","반안살","장성살"].some(w=>s.includes(w)||s===w);
                                            return <span key={si} style={{fontSize:11,fontWeight:700,color:isGil?"#2F9E44":"#C92A2A",whiteSpace:"nowrap"}}>{s}</span>;
                                          })}
                                        </div>
                                      ) : <span style={{fontSize:11,color:"#ccc"}}>—</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 지장간 */}
                              <tr>
                                <td style={labelTd}>지장간</td>
                                {cols.map((col,ci)=>{
                                  if (col.isUnknown) return <td key={ci} style={smallTd(ci===1)}><span style={{color:"#ccc"}}>—</span></td>;
                                  const jjg=BRANCH_INFO[col.ji]?.jijanggan||[];
                                  return (
                                    <td key={ci} style={smallTd(ci===1)}>
                                      <span style={{fontSize:15,letterSpacing:1,fontWeight:600}}>
                                        {jjg.map((c:string,jIdx:number)=>(
                                          <span key={jIdx} style={{color:OHAENG_STYLE[STEM_OHAENG[c]||"토"]?.color||"#333",marginRight:jIdx<jjg.length-1?4:0}}>{c}</span>
                                        ))}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* 12운성 */}
                              <tr>
                                <td style={labelTd}>12운성</td>
                                {cols.map((col,ci)=>(
                                  <td key={ci} style={smallTd(ci===1)}>
                                    <span style={{fontSize:15,color:col.isUnknown?"#ccc":"#222222",fontWeight:600}}>{col.isUnknown?"—":TWELVE_FORTUNE[dayGan]?.[col.ji]||"—"}</span>
                                  </td>
                                ))}
                              </tr>
                              {/* 12신살 */}
                              <tr>
                                <td style={labelTd}>12신살</td>
                                {cols.map((col,ci)=>(
                                  <td key={ci} style={smallTd(ci===1)}>
                                    <span style={{fontSize:15,color:col.isUnknown?"#ccc":"#222222",fontWeight:600}}>{col.isUnknown?"—":TWELVE_SPIRIT[yearGroup]?.[col.ji]||"—"}</span>
                                  </td>
                                ))}
                              </tr>
                              {/* 공망 */}
                              {(() => {
                                const gm = calcGongmang(dayGan, dayBranch);
                                return (
                                  <tr>
                                    <td style={labelTd}>공망</td>
                                    {cols.map((col,ci)=>{
                                      if (col.isUnknown) return <td key={ci} style={smallTd(ci===1)}><span style={{color:"#ccc"}}>—</span></td>;
                                      const isGm = gm.includes(col.ji);
                                      return (
                                        <td key={ci} style={smallTd(ci===1)}>
                                          <span style={{fontSize:15,fontWeight:isGm?700:600,color:isGm?"#C92A2A":"#222222"}}>
                                            {isGm?"공망":"—"}
                                          </span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })()}
                            </tbody>
                          </table>
                        </div>
                        {/* 공망 안내 */}
                        {(() => {
                          const gm = calcGongmang(dayGan, dayBranch);
                          const gmInChart = cols.filter(c=>gm.includes(c.ji)).map(c=>JI_KOR_MAP[c.ji]||c.ji);
                          return (
                            <div style={{marginTop:10,padding:"8px 12px",background:"#FFF5F5",borderRadius:8,fontSize:12,color:"#C92A2A",border:"1px solid #FFCCCC"}}>
                              <strong>공망(空亡)</strong>: {JI_KOR_MAP[gm[0]]||gm[0]}·{JI_KOR_MAP[gm[1]]||gm[1]}
                              {gmInChart.length>0 ? ` — 원국 ${gmInChart.join("·")}지가 공망에 해당, 해당 주의 기운이 약해집니다.` : " — 원국에 공망 지지 없음"}
                            </div>
                          );
                        })()}

                        {/* 용신/희신/기신/格局 태그 */}
                        {(scores || wuxing) && (
                          <div style={{marginTop:14,display:"flex",gap:6,flexWrap:"wrap"}}>
                            {(() => {
                              const gn = detail.geokguk ||
                                (wuxing?.extended_analysis?.forceteller_ohaeng as any)?.geokguk?.geok_name || '';
                              return gn ? <Tag $type="blue">格局: {gn}</Tag> : null;
                            })()}
                            <Tag $type="neutral">{(detail.strength || wuxing?.strength)==="strong"?"신강":"신약"}</Tag>
                          </div>
                        )}
                        {/* 5종 용신 표시 */}
                        {(scores || wuxing) && (() => {
                          const GAN_KOR: Record<string,string> = {
                            甲:'갑',乙:'을',丙:'병',丁:'정',戊:'무',己:'기',庚:'경',辛:'신',壬:'임',癸:'계'
                          };
                          const ganKor = (gc:string) => GAN_KOR[gc] || gc;
                          // 오행 → 원국 천간 글자 탐색 (천간 우선, 없으면 빈 문자열)
                          const OE_STEMS: Record<string,string[]> = {
                            '목':['甲','乙'],'화':['丙','丁'],'토':['戊','己'],'금':['庚','辛'],'수':['壬','癸']
                          };
                          const findStemChar = (oe: string, knownChar?: string): string => {
                            if (knownChar && GAN_KOR[knownChar]) return knownChar;
                            if (!oe || !palja) return '';
                            const targets = OE_STEMS[oe] || [];
                            const stems = [
                              palja.year_pillar?.stem, palja.month_pillar?.stem,
                              palja.day_pillar?.stem, palja.hour_pillar?.stem,
                            ];
                            for (const s of stems) { if (s && targets.includes(s)) return s; }
                            return '';
                          };
                          const charStr = (oe: string, knownChar?: string): string => {
                            const gc = findStemChar(oe, knownChar);
                            return gc ? `${ganKor(gc)}${oe}` : oe;
                          };

                          const fiveYS = (detail.five_yongsin as any) || {};
                          const johuFY = fiveYS.johu || {};
                          const eokbuFY = fiveYS.eokbu || {};
                          const johuStatusMap: Record<string,string> = {
                            '과다':'조열', '극과다':'극조열', '난':'난조', '조':'조열',
                            '부족':'한랭', '극부족':'극한랭', '한':'한랭', '습':'습냉',
                          };
                          const eokbuGod = detail.eokbu_god || detail.use_god || wuxing?.use_god || '';
                          const eokbuStr = charStr(eokbuGod, eokbuFY.god_char);
                          const johuOe1  = detail.johu_god || '';
                          const johuStr1 = charStr(johuOe1, johuFY.god_char);
                          const johuOe2  = detail.johu_secondary || johuFY.secondary || '';
                          const johuStr2 = johuOe2 ? charStr(johuOe2, johuFY.secondary_char) : '';
                          const johuStr  = johuStr1 + (johuStr2 ? ` · ${johuStr2}` : '');
                          const avoidOe  = detail.eokbu_avoid || detail.avoid_god || wuxing?.avoid_god || '';
                          const avoidStr = charStr(avoidOe, eokbuFY.avoid_char);
                          const heeshinOe = detail.heeshin || '';
                          const heeshinStr = charStr(heeshinOe);
                          const johuAvoid: string[] = johuFY.avoid || [];
                          const johuAvoidStr = johuAvoid.filter((x:string)=>x!==avoidOe).map((x:string)=>charStr(x)).join('·');
                          const needArr: string[] = Array.isArray(detail.johu_need) ? detail.johu_need : [];
                          const johuGc1 = johuFY.god_char || ''; const johuGc2 = johuFY.secondary_char || '';
                          const needGcs = [johuGc1, johuGc2];
                          const needStr = needArr.map((oe:string, i:number) => charStr(oe, needGcs[i])).join('·');
                          const johuAvoidFull = johuAvoid.map((x:string)=>charStr(x)).join('·');
                          const mappedStatus = detail.johu_status ? (johuStatusMap[detail.johu_status] || '') : '';
                          // 병약/통관/전왕 char
                          const byungyakFY = fiveYS.byungyak || {};
                          const tonggwanFY = fiveYS.tonggwan || {};
                          const jeonwangFY = fiveYS.jeonwang || {};
                          const byDrug = detail.byungyak?.drug || byungyakFY.drug || '';
                          const byDrugStr = charStr(byDrug, byungyakFY.drug_char);
                          const byDisease = detail.byungyak?.disease || byungyakFY.disease || '';
                          const byDiseaseStr = charStr(byDisease, byungyakFY.disease_char);
                          const tgGod = detail.tonggwan?.god || tonggwanFY.god || '';
                          const tgStr = charStr(tgGod, tonggwanFY.god_char);
                          const jwGod = detail.jeonwang?.god || jeonwangFY.god || '';
                          const jwStr = charStr(jwGod, jeonwangFY.god_char);
                          return (
                            <div style={{marginTop:10,padding:"10px 14px",background:"#F8FFF4",border:"1px solid #8CE99A",borderRadius:8,fontSize:13}}>
                              <div style={{fontWeight:700,marginBottom:6,color:"#2F9E44"}}>용신 / 기신</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:"6px 14px"}}>
                                {eokbuGod && (
                                  <span><strong style={{color:"#2F9E44"}}>억부용신</strong>: {eokbuStr}</span>
                                )}
                                {johuStr && (
                                  <span><strong style={{color:"#1971C2"}}>조후용신</strong>: {johuStr}</span>
                                )}
                                {avoidOe && (
                                  <span>
                                    <strong style={{color:"#C92A2A"}}>기신</strong>: {avoidStr}
                                    {johuAvoidStr && <span style={{marginLeft:4,color:"#999"}}>/ 조후기피: {johuAvoidStr}</span>}
                                  </span>
                                )}
                                {!avoidOe && johuAvoid.length > 0 && (
                                  <span><strong style={{color:"#C92A2A"}}>조후기피</strong>: {johuAvoidFull}</span>
                                )}
                                {heeshinOe && (
                                  <span><strong style={{color:"#5C7CFA"}}>희신</strong>: {heeshinStr}</span>
                                )}
                              </div>
                              {/* 병약/통관/전왕 */}
                              {(byDrug || tgGod || jwGod) && (
                                <div style={{marginTop:6,display:"flex",flexWrap:"wrap",gap:"6px 14px",fontSize:12,color:"#666"}}>
                                  {byDrug && <span>병약용신: {byDrugStr} (병: {byDiseaseStr})</span>}
                                  {tgGod && <span>통관용신: {tgStr}</span>}
                                  {jwGod && <span>전왕용신: {jwStr}</span>}
                                </div>
                              )}
                              {/* 조후 상세 */}
                              {needArr.length > 0 && (
                                <div style={{marginTop:6,fontSize:12,color:"#1864AB"}}>
                                  <strong>조후</strong>
                                  {needStr && <span style={{marginLeft:6}}>필요: {needStr}</span>}
                                  {johuAvoidFull && <span style={{marginLeft:6}}>기피: {johuAvoidFull}</span>}
                                  {mappedStatus && <span style={{marginLeft:6,color:"#888"}}>({mappedStatus})</span>}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {detail.yongshin_rationale && (
                          <div style={{marginTop:10,background:"#F8F9FA",border:"1px solid var(--border-light)",borderRadius:10,padding:"10px 14px",fontSize:13,lineHeight:1.7}}>
                            <span style={{fontWeight:700,color:"var(--text-secondary)"}}>용신 근거 — </span>{detail.yongshin_rationale}
                          </div>
                        )}
                      </ResultCard>
                    );
                  })()}

                  {/* 합·충·형·파·해 — 만세력 바로 아래 */}
                  {(scores || hasHapChung) && (
                    <ResultCard>
                      <CardTitle>천간합충 · 지지합충형파해원진</CardTitle>
                      {!hasHapChung ? (
                        <div style={{color:"var(--text-secondary)",fontSize:13}}>원국에 합·충·형·파·해 없음</div>
                      ) : (
                        <>
                          {hapChung.cheongan_hap?.length>0    && (<><SubLabel>천간합(天干合)</SubLabel><TagRow>{hapChung.cheongan_hap.map((s:string,i:number)=><Tag key={i} $type="good">{s}</Tag>)}</TagRow></>)}
                          {hapChung.cheongan_chung?.length>0  && (<><SubLabel>천간충(天干沖)</SubLabel><TagRow>{hapChung.cheongan_chung.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jiji_hap?.length>0        && (<><SubLabel>지지합·육합(六合)</SubLabel><TagRow>{hapChung.jiji_hap.map((s:string,i:number)=><Tag key={i} $type="good">{s}</Tag>)}</TagRow></>)}
                          {hapChung.samhap?.length>0           && (<><SubLabel>삼합·반합(三合)</SubLabel><TagRow>{hapChung.samhap.map((s:string,i:number)=><Tag key={i} $type="good">{s}</Tag>)}</TagRow></>)}
                          {hapChung.banghap?.length>0          && (<><SubLabel>방합(方合)</SubLabel><TagRow>{hapChung.banghap.map((s:string,i:number)=><Tag key={i} $type="good">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jiji_chung?.length>0       && (<><SubLabel>지지충(地支沖)</SubLabel><TagRow>{hapChung.jiji_chung.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jiji_hyung?.length>0       && (<><SubLabel>지지형·삼형(刑)</SubLabel><TagRow>{hapChung.jiji_hyung.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jaji_hyeong?.length>0      && (<><SubLabel>자형살(自刑殺)</SubLabel><TagRow>{hapChung.jaji_hyeong.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jiji_pa?.length>0          && (<><SubLabel>지지파(破)</SubLabel><TagRow>{hapChung.jiji_pa.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.jiji_hae?.length>0         && (<><SubLabel>지지해(害)</SubLabel><TagRow>{hapChung.jiji_hae.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                          {hapChung.weonjin?.length>0          && (<><SubLabel>원진살(怨嗔殺)</SubLabel><TagRow>{hapChung.weonjin.map((s:string,i:number)=><Tag key={i} $type="bad">{s}</Tag>)}</TagRow></>)}
                        </>
                      )}
                    </ResultCard>
                  )}

                  {/* 신살 · 길성 — 맨 아래 */}
                  {scores && (() => {
                    const ss = detail?.sinsal || {};
                    const present: any[] = ss.present || [];
                    const gilItems   = present.filter((p:any) => p.type === 'gil');
                    const hyungItems = present.filter((p:any) => p.type === 'hyung');
                    const neutralItems = present.filter((p:any) => p.type === 'neutral');
                    const gongmang: string[] = ss.gongmang || [];
                    const group: string = ss.group || '';

                    const SinsalBadge = ({item}: {item:any}) => {
                      const isGil = item.type === 'gil';
                      const isHyung = item.type === 'hyung';
                      const bg    = isGil ? '#EBFBEE' : isHyung ? '#FFF0F0' : '#FFF9DB';
                      const color = isGil ? '#2F9E44' : isHyung ? '#C92A2A' : '#E67700';
                      return (
                        <div style={{display:'inline-flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:8,background:bg,border:`1px solid ${color}33`,marginBottom:4,marginRight:4}}>
                          <span style={{fontSize:16,fontWeight:800,color}}>{item.char}</span>
                          <div style={{lineHeight:1.2}}>
                            <div style={{fontSize:12,fontWeight:700,color}}>{item.name}</div>
                            <div style={{fontSize:9,color:'#868E96'}}>{item.pos}</div>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <ResultCard>
                        <CardTitle>신살 · 길성</CardTitle>
                        <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:12}}>
                          {group && <span style={{fontSize:11,padding:'3px 10px',borderRadius:20,background:'#F3F0FF',color:'#6741D9',border:'1.5px solid #9775FA44'}}>삼합국: {group}</span>}
                          {gongmang.length>0 && <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:'#FFF9DB',color:'#E67700',border:'1.5px solid #FFD43B55'}}>공망: {gongmang.join('·')}</span>}
                        </div>
                        {gilItems.length>0 && (
                          <div style={{marginBottom:12}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#2F9E44',marginBottom:6}}>길신 (吉神) {gilItems.length}개</div>
                            <div style={{display:'flex',flexWrap:'wrap'}}>
                              {gilItems.map((item:any,i:number)=><SinsalBadge key={i} item={item}/>)}
                            </div>
                          </div>
                        )}
                        {hyungItems.length>0 && (
                          <div style={{marginBottom:12}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#C92A2A',marginBottom:6}}>흉살 (凶殺) {hyungItems.length}개</div>
                            <div style={{display:'flex',flexWrap:'wrap'}}>
                              {hyungItems.map((item:any,i:number)=><SinsalBadge key={i} item={item}/>)}
                            </div>
                          </div>
                        )}
                        {neutralItems.length>0 && (
                          <div style={{marginBottom:12}}>
                            <div style={{fontSize:11,fontWeight:700,color:'#E67700',marginBottom:6}}>특수격·살</div>
                            <div style={{display:'flex',flexWrap:'wrap'}}>
                              {neutralItems.map((item:any,i:number)=><SinsalBadge key={i} item={item}/>)}
                            </div>
                          </div>
                        )}
                        {present.length===0 && <div style={{fontSize:13,color:'var(--text-secondary)',textAlign:'center',padding:'16px 0'}}>해당 신살 없음</div>}
                      </ResultCard>
                    );
                  })()}

                  {!scores && (
                    <ResultCard>
                      <div style={{textAlign:"center",padding:"20px 0",color:"var(--text-secondary)",fontSize:14}}>
                        상단 "사주 엔진 분석" 버튼을 눌러 신살·형충파해 분석을 시작하세요.
                      </div>
                    </ResultCard>
                  )}
                </>
              )}

              {/* ── 탭: 오행분석 ─────────────────────────────── */}
              {tab === "ohaeng" && (
                <ResultCard>
                  <CardTitle style={{ color: "#2F9E44" }}>오행 분석</CardTitle>
                  {!wuxing ? (
                    <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>사주 데이터가 없습니다.</div>
                  ) : (() => {
                    const extAnalysis = wuxing?.extended_analysis as any;
                    const dg = palja?.day_pillar?.stem || "";
                    const dayOe = STEM_OHAENG[dg] || "목";

                    const OE_COLOR: Record<string,string> = {
                      목:"#2E86AB", 화:"#E63946", 토:"#F4A261", 금:"#A8A8A8", 수:"#1D3557",
                    };
                    const OE_BG: Record<string,string> = {
                      목:"#EBF4F9", 화:"#FDE8EA", 토:"#FEF4E8", 금:"#F5F5F5", 수:"#E8EDF3",
                    };
                    const OE_LABEL: Record<string,string> = { 목:"목(木)", 화:"화(火)", 토:"토(土)", 금:"금(金)", 수:"수(水)" };
                    const OE_KAN:   Record<string,string> = { 목:"木", 화:"火", 토:"土", 금:"金", 수:"水" };

                    const CYCLE5 = ["목","화","토","금","수"];
                    const gen5 = (a:string,b:string) => CYCLE5[(CYCLE5.indexOf(a)+1)%5]===b;
                    const ctl5 = (a:string,b:string) => CYCLE5[(CYCLE5.indexOf(a)+2)%5]===b;

                    const ftOe  = extAnalysis?.forceteller_ohaeng as any;
                    const dm    = extAnalysis?.diagram_matrix as any;
                    type DmKey = "opt_off_off"|"opt_on_off"|"opt_off_on"|"opt_on_on";
                    const dmKey: DmKey = `opt_${applyHap?"on":"off"}_${applyWeight?"on":"off"}` as DmKey;
                    const dmData = dm?.[dmKey] as any;
                    const ftPct  = (dmData?.pct || ftOe?.pct  || {}) as Record<string,number>;
                    const oeList = ["목","화","토","금","수"] as const;
                    const oePct  = Object.fromEntries(oeList.map(o => [o, Math.round(ftPct[o]||0)])) as Record<string,number>;

                    const topOe = oeList.reduce((a,b) => oePct[a]>=oePct[b] ? a : b);
                    const getOeStatus = (p:number) => p<10?"부족":p<20?"적정":p<30?"발달":"과다";
                    const statusColor  = (s:string) => ({부족:"#74C0FC",적정:"#51CF66",발달:"#FF922B",과다:"#E03131"}[s]||"#868E96");

                    const ALL_STARS = ["비견","겁재","식신","상관","편재","정재","편관","정관","편인","정인"];
                    const STAR_HANJA: Record<string,string> = {비견:"比肩",겁재:"劫財",식신:"食神",상관:"傷官",편재:"偏財",정재:"正財",편관:"偏官",정관:"正官",편인:"偏印",정인:"正印"};
                    const STAR_COLOR: Record<string,string> = {
                      비견:"#6C757D", 겁재:"#9BA3AD",
                      식신:"#5C9E5C", 상관:"#86C486",
                      편재:"#C8862A", 정재:"#E8B040",
                      편관:"#B84040", 정관:"#D06060",
                      편인:"#4472A8", 정인:"#6898CC",
                    };

                    const starNames = (oe:string): [string,string] => {
                      if (oe===dayOe)       return ["비견","겁재"];
                      if (gen5(dayOe,oe))   return ["식신","상관"];
                      if (ctl5(dayOe,oe))   return ["편재","정재"];
                      if (gen5(oe,dayOe))   return ["편인","정인"];
                      return ["편관","정관"];
                    };

                    const dmTenStar = dmData?.ten_star_pct as Record<string,number>|undefined;
                    const backendStarPct = (dmTenStar && Object.keys(dmTenStar).length > 0
                      ? dmTenStar : (ftOe?.ten_star_pct || {})) as Record<string,number>;
                    const starPct: Record<string,number> = {};
                    ALL_STARS.forEach(s => { starPct[s] = Math.round(backendStarPct[s]||0); });
                    const topStar = ALL_STARS.filter(s=>starPct[s]>0).reduce((a,b)=>starPct[a]>=starPct[b]?a:b,"정인");

                    // 도넛 컴포넌트
                    const Doughnut = ({ slices, size=130, thick=20, label, sublabel, labelColor }:
                      {slices:{pct:number;color:string}[];size?:number;thick?:number;label:string;sublabel:string;labelColor:string}) => {
                      const r=(size-thick)/2, cx=size/2, cy=size/2, circ=2*Math.PI*r;
                      let offset=0;
                      const arcs = slices.filter(s=>s.pct>0).map((s,i)=>{
                        const len=circ*s.pct/100;
                        const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none"
                          stroke={s.color} strokeWidth={thick}
                          strokeDasharray={`${len} ${circ-len}`}
                          strokeDashoffset={-offset}
                          style={{transform:"rotate(-90deg)",transformOrigin:"center"}}/>;
                        offset+=len; return el;
                      });
                      return (
                        <div style={{position:"relative",width:size,height:size}}>
                          <svg width={size} height={size}>
                            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E9ECEF" strokeWidth={thick}/>
                            {arcs}
                          </svg>
                          <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                            <span style={{fontSize:15,fontWeight:800,color:labelColor,lineHeight:1.1}}>{label}</span>
                            <span style={{fontSize:10,color:"#868E96",lineHeight:1.2}}>{sublabel}</span>
                          </div>
                        </div>
                      );
                    };

                    const OE_STAR_MAP: Record<string,[string,string]> = {
                      목:starNames("목"), 화:starNames("화"), 토:starNames("토"), 금:starNames("금"), 수:starNames("수"),
                    };
                    // 십성 → 오행 역매핑 (일간 기준으로 달라지므로 OE_STAR_MAP에서 역산)
                    const STAR_OE: Record<string,string> = {};
                    oeList.forEach(oe => { STAR_OE[OE_STAR_MAP[oe][0]] = oe; STAR_OE[OE_STAR_MAP[oe][1]] = oe; });

                    const oeSlices   = oeList.map(o=>({pct:oePct[o],color:OE_COLOR[o]}));
                    // 십성 도넛: 오행색 사용 (비견/겁재 → 목 색, 식신/상관 → 화 색, ...)
                    const starSlices  = ALL_STARS.filter(s=>starPct[s]>0).map(s=>({pct:starPct[s],color:OE_COLOR[STAR_OE[s]]||STAR_COLOR[s]}));
                    const ROW_H = 38, HEAD_H = 34;

                    // 오각형
                    const relLabel = (oe:string) => {
                      if (oe===dayOe) return "비겁";
                      if (gen5(oe,dayOe)) return "인성";
                      if (gen5(dayOe,oe)) return "식상";
                      if (ctl5(dayOe,oe)) return "재성";
                      return "관성";
                    };
                    const SZ=340, CX=170, CY=170, R=125, NR=42;
                    const dayOeIdx2 = oeList.indexOf(dayOe as any);
                    const pts = oeList.map((_,i)=>{ const a=((i - dayOeIdx2)*72-90)*Math.PI/180; return {x:CX+R*Math.cos(a),y:CY+R*Math.sin(a)}; });
                    const edgePt=(si:number,ei:number,off:number)=>{
                      const s=pts[si],e=pts[ei],dx=e.x-s.x,dy=e.y-s.y,len=Math.sqrt(dx*dx+dy*dy);
                      return {x:s.x+(dx/len)*off,y:s.y+(dy/len)*off};
                    };
                    const dist=(si:number,ei:number)=>{ const s=pts[si],e=pts[ei]; return Math.sqrt((e.x-s.x)**2+(e.y-s.y)**2); };
                    const genArr=oeList.map((_,i)=>{ const ni=(i+1)%5; return {s:edgePt(i,ni,NR+7),e:edgePt(i,ni,dist(i,ni)-NR-7)}; });
                    const ctlArr=oeList.map((_,i)=>{ const ni=(i+2)%5; return {s:edgePt(i,ni,NR+7),e:edgePt(i,ni,dist(i,ni)-NR-7)}; });

                    return (
                      <>
                        {/* 체크박스 옵션 */}
                        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16,padding:"12px 14px",background:"#F8F9FA",borderRadius:10,border:"1px solid #E9ECEF"}}>
                          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                            <input type="checkbox" checked={applyHap} onChange={()=>setApplyHap(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                            <span>합에 따른 오행 변화 적용</span>
                            <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyHap?"#EBFBEE":"#F1F3F5",color:applyHap?"#2F9E44":"#ADB5BD"}}>{applyHap?"적용중":"미적용"}</span>
                          </label>
                          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                            <input type="checkbox" checked={applyWeight} onChange={()=>setApplyWeight(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                            <span>조후와 궁성 보정값 적용</span>
                            <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyWeight?"#EBFBEE":"#F1F3F5",color:applyWeight?"#2F9E44":"#ADB5BD"}}>{applyWeight?"적용중":"미적용"}</span>
                          </label>
                          <div style={{fontSize:11,color:"#ADB5BD",lineHeight:1.5}}>
                            {!applyHap&&!applyWeight&&"기본값: 합형충파해 미적용 + 조후궁성 미적용"}
                            {applyHap&&!applyWeight&&"합형충파해만 적용한 오행 비율"}
                            {!applyHap&&applyWeight&&"조후와 궁성 보정값만 적용한 오행 비율"}
                            {applyHap&&applyWeight&&"합형충파해 + 조후궁성 보정 모두 합산한 오행 비율"}
                          </div>
                        </div>

                        {/* 도넛 2개 */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
                          <div style={{background:"#F8F9FA",borderRadius:14,padding:"16px 14px",display:"flex",flexDirection:"column",alignItems:"center"}}>
                            <Doughnut slices={oeSlices} label={OE_KAN[topOe]} sublabel={OE_LABEL[topOe].split("(")[0]} labelColor={OE_COLOR[topOe]}/>
                          </div>
                          <div style={{background:"#F8F9FA",borderRadius:14,padding:"16px 14px",display:"flex",flexDirection:"column",alignItems:"center"}}>
                            <Doughnut slices={starSlices} label={topStar} sublabel={STAR_HANJA[topStar]} labelColor={OE_COLOR[STAR_OE[topStar]]||STAR_COLOR[topStar]}/>
                          </div>
                        </div>

                        {/* 오행/십성 테이블 */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
                          <div style={{background:"#F8F9FA",borderRadius:14,overflow:"hidden"}}>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                              <thead>
                                <tr style={{borderBottom:"1.5px solid #E9ECEF",height:HEAD_H}}>
                                  <th style={{padding:"0 8px",color:"#495057",fontWeight:700,textAlign:"center"}}>오행</th>
                                  <th style={{padding:"0 8px",color:"#495057",fontWeight:700,textAlign:"right"}}>비율</th>
                                  <th style={{padding:"0 8px",textAlign:"center"}}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {oeList.map(oe=>{
                                  const p=oePct[oe]; const st=getOeStatus(p);
                                  return (
                                    <tr key={oe} style={{borderBottom:"1px solid #F1F3F5",height:ROW_H*2}}>
                                      <td style={{padding:"0 8px",color:OE_COLOR[oe],fontWeight:700,textAlign:"center",verticalAlign:"middle"}}>{OE_LABEL[oe]}</td>
                                      <td style={{padding:"0 8px",textAlign:"right",fontWeight:700,color:"#212529",verticalAlign:"middle"}}>{p}%</td>
                                      <td style={{padding:"0 8px",textAlign:"center",verticalAlign:"middle"}}>
                                        <span style={{fontSize:10,fontWeight:700,color:statusColor(st),background:statusColor(st)+"22",borderRadius:8,padding:"2px 7px"}}>{st}</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div style={{background:"#F8F9FA",borderRadius:14,overflow:"hidden"}}>
                            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                              <thead>
                                <tr style={{borderBottom:"1.5px solid #E9ECEF",height:HEAD_H}}>
                                  <th style={{padding:"0 8px",color:"#495057",fontWeight:700,textAlign:"center"}}>십성</th>
                                  <th style={{padding:"0 8px",color:"#495057",fontWeight:700,textAlign:"right"}}>비율</th>
                                </tr>
                              </thead>
                              <tbody>
                                {oeList.map(oe=>{
                                  const [s1,s2]=OE_STAR_MAP[oe];
                                  const p1=starPct[s1]||0, p2=starPct[s2]||0;
                                  const c=OE_COLOR[oe];
                                  return (
                                    <React.Fragment key={oe}>
                                      <tr style={{borderBottom:"1px solid #F1F3F5",height:ROW_H}}>
                                        <td style={{padding:"0 8px",textAlign:"center",color:p1>0?c:"#ADB5BD",fontWeight:p1>0?600:400,verticalAlign:"middle"}}>{p1>0?`${s1}(${STAR_HANJA[s1]})`:"-"}</td>
                                        <td style={{padding:"0 8px",textAlign:"right",color:p1>0?c:"#ADB5BD",fontWeight:p1>0?700:400,verticalAlign:"middle"}}>{p1>0?`${p1}%`:""}</td>
                                      </tr>
                                      <tr style={{borderBottom:"1px solid #F1F3F5",height:ROW_H}}>
                                        <td style={{padding:"0 8px",textAlign:"center",color:p2>0?c:"#ADB5BD",fontWeight:p2>0?600:400,verticalAlign:"middle"}}>{p2>0?`${s2}(${STAR_HANJA[s2]})`:"-"}</td>
                                        <td style={{padding:"0 8px",textAlign:"right",color:p2>0?c:"#ADB5BD",fontWeight:p2>0?700:400,verticalAlign:"middle"}}>{p2>0?`${p2}%`:""}</td>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* 오각형 다이어그램 */}
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                          <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)",marginBottom:4}}>
                            나의 오행: <span style={{color:OE_COLOR[dayOe]}}>{OE_LABEL[dayOe]}</span>
                          </div>
                          <div style={{display:"flex",gap:16,fontSize:11,marginBottom:2}}>
                            <span style={{color:"#5B9BD5"}}>→ 생(生)</span>
                            <span style={{color:"#D05050"}}>→ 극(剋)</span>
                          </div>
                          <svg viewBox={`0 0 ${SZ} ${SZ}`} style={{width:"100%",maxWidth:360,height:"auto",display:"block"}}>
                            <defs>
                              <marker id="mB2" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><polygon points="0 0,8 4,0 8" fill="#5B9BD5"/></marker>
                              <marker id="mR2" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><polygon points="0 0,8 4,0 8" fill="#D05050"/></marker>
                            </defs>
                            {ctlArr.map((a,i)=><line key={`c${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#D05050" strokeWidth={1.8} markerEnd="url(#mR2)"/>)}
                            {genArr.map((a,i)=><line key={`g${i}`} x1={a.s.x} y1={a.s.y} x2={a.e.x} y2={a.e.y} stroke="#5B9BD5" strokeWidth={1.8} markerEnd="url(#mB2)"/>)}
                            {oeList.map((oe,i)=>{
                              const p=pts[i], oeP=oePct[oe]||0, isDay=oe===dayOe;
                              const fillH=NR*2*Math.min(oeP,100)/100;
                              return <g key={oe}>
                                <clipPath id={`acp${i}`}><rect x={p.x-NR} y={p.y+NR-fillH} width={NR*2} height={fillH+2}/></clipPath>
                                <circle cx={p.x} cy={p.y} r={NR} fill={OE_BG[oe]} stroke={isDay?OE_COLOR[oe]:"#D8D8D8"} strokeWidth={isDay?3:1.5}/>
                                <circle cx={p.x} cy={p.y} r={NR-1} fill={OE_COLOR[oe]} opacity={0.25} clipPath={`url(#acp${i})`}/>
                                <text x={p.x} y={p.y-10} textAnchor="middle" fontSize={11} fontWeight={700} fill={OE_COLOR[oe]}>
                                  {OE_LABEL[oe].split("(")[0]}({relLabel(oe)})
                                </text>
                                <text x={p.x} y={p.y+8} textAnchor="middle" fontSize={13} fontWeight={800} fill={OE_COLOR[oe]}>
                                  {oeP}%
                                </text>
                              </g>;
                            })}
                          </svg>
                        </div>
                      </>
                    );
                  })()}
                </ResultCard>
              )}

              {/* ── 탭: 대운별 운세 ──────────────────────────── */}
              {tab === "daeun" && (
                <div style={{ padding: "0 0 12px" }}>

                  {/* ── 연애/결혼 성향 프로파일 ── */}
                  {loveProfile && (
                    <ResultCard style={{ margin: "12px 16px 0", background: "#FFF5F8", border: "1.5px solid #FFC9D4" }}>
                      <CardTitle style={{ color: "#C2255C" }}>💕 연애·결혼 성향 프로파일 (格局 기반)</CardTitle>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <SubLabel>연애 스타일</SubLabel>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#C2255C", marginBottom: 4 }}>
                            {loveProfile.style}
                          </div>
                          <div style={{ fontSize: 12, color: "#495057", lineHeight: 1.6 }}>{loveProfile.style_desc}</div>
                        </div>
                        <div>
                          <SubLabel>결혼 경향</SubLabel>
                          <div style={{ fontSize: 14, fontWeight: 700,
                            color: loveProfile.marry_tendency === '이혼주의' ? '#C92A2A'
                              : loveProfile.marry_tendency === '결혼운 양호' ? '#2F9E44'
                              : '#495057',
                            marginBottom: 4
                          }}>
                            {loveProfile.marry_tendency}
                          </div>
                          <div style={{ fontSize: 12, color: "#495057" }}>
                            연애결혼 경향: {loveProfile.love_marriage_tendency}
                          </div>
                        </div>
                      </div>
                      {loveProfile.day_oe_note && (
                        <div style={{ fontSize: 12, color: "#495057", background: "#fff", borderRadius: 6, padding: "6px 10px", marginBottom: 8, border: "1px solid #FFD6E0" }}>
                          <strong>일간 오행 특성:</strong> {loveProfile.day_oe_note}
                        </div>
                      )}
                      {loveProfile.risk_factors?.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <SubLabel style={{ color: "#C92A2A" }}>주의 요소</SubLabel>
                          {loveProfile.risk_factors.map((r: string, i: number) => (
                            <div key={i} style={{ fontSize: 12, color: "#C92A2A", padding: "2px 0" }}>⚠ {r}</div>
                          ))}
                        </div>
                      )}
                      {loveProfile.strengths?.length > 0 && (
                        <div>
                          <SubLabel style={{ color: "#2F9E44" }}>길상 요소</SubLabel>
                          {loveProfile.strengths.map((s: string, i: number) => (
                            <div key={i} style={{ fontSize: 12, color: "#2F9E44", padding: "2px 0" }}>✓ {s}</div>
                          ))}
                        </div>
                      )}
                    </ResultCard>
                  )}

                  {/* ── 재물·직업 성향 프로파일 ── */}
                  {wealthProfile && (
                    <ResultCard style={{ margin: "12px 16px 0", background: "#F0FFF4", border: "1.5px solid #8CE99A" }}>
                      <CardTitle style={{ color: "#2B8A3E" }}>💼 재물·직업 성향 프로파일 (格局 기반)</CardTitle>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                        <div>
                          <SubLabel>재물 기반 성향</SubLabel>
                          <div style={{ fontSize: 15, fontWeight: 700,
                            color: wealthProfile.wealth_base === '대부자형' ? '#2B8A3E'
                              : wealthProfile.wealth_base?.includes('주의') || wealthProfile.wealth_base?.includes('군겁') ? '#C92A2A'
                              : '#2B8A3E',
                            marginBottom: 4
                          }}>
                            {wealthProfile.wealth_base}
                          </div>
                          <div style={{ fontSize: 12, color: "#495057", lineHeight: 1.6 }}>{wealthProfile.wealth_desc}</div>
                        </div>
                        <div>
                          <SubLabel>사업·직업 성향</SubLabel>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#1864AB", marginBottom: 4 }}>
                            {wealthProfile.biz_type}
                          </div>
                          <div style={{ fontSize: 12, color: "#495057" }}>{wealthProfile.biz_type_desc}</div>
                        </div>
                      </div>
                      {wealthProfile.talent_note && (
                        <div style={{ fontSize: 12, color: "#495057", background: "#fff", borderRadius: 6, padding: "6px 10px", marginBottom: 8, border: "1px solid #B2F2BB" }}>
                          <strong>일간 오행 재능:</strong> {wealthProfile.talent_note}
                        </div>
                      )}
                      {wealthProfile.talent_fields?.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <SubLabel>재능 분야</SubLabel>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {wealthProfile.talent_fields.map((f: string, i: number) => (
                              <span key={i} style={{ fontSize: 11, background: "#D3F9D8", color: "#2B8A3E", padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {wealthProfile.risk_factors?.length > 0 && (
                        <div>
                          <SubLabel style={{ color: "#C92A2A" }}>주의 요소</SubLabel>
                          {wealthProfile.risk_factors.map((r: string, i: number) => (
                            <div key={i} style={{ fontSize: 12, color: "#C92A2A", padding: "2px 0" }}>⚠ {r}</div>
                          ))}
                        </div>
                      )}
                    </ResultCard>
                  )}

                  {daeunScores.length === 0 ? (
                    <ResultCard>
                      <div style={{ textAlign: "center", padding: "24px 0", color: "var(--text-secondary)", fontSize: 14 }}>
                        {scores ? "대운 점수 데이터가 없습니다." : "상단 '사주 엔진 분석' 버튼을 먼저 눌러주세요."}
                      </div>
                    </ResultCard>
                  ) : (
                    <>
                      {/* ── 1단: 대운 타임라인 ── */}
                      <ResultCard style={{ margin: "12px 16px 0" }}>
                        <SubLabel style={{ marginBottom: 8 }}>대운 (10년 주기) — 클릭하면 세부 정보가 표시됩니다</SubLabel>
                        <PillarTimelineWrap>
                          {daeunScores.map((d: any, idx: number) => {
                            const ganOe = STEM_OHAENG[d.gan] || "토";
                            const jiOe  = BRANCH_OHAENG[d.ji] || "토";
                            const dayGan = palja?.day_pillar?.stem || "";
                            const tenStar = calcTenStar(dayGan, d.gan);
                            const jiStar  = calcTenStar(dayGan, d.ji);
                            const unju12  = (TWELVE_FORTUNE[dayGan] as Record<string,string>)?.[d.ji] || "";
                            const currentAge = new Date().getFullYear() - (consult.birth_year || 0) + 1;
                            const isCurrent = d.start_age != null && d.end_age != null
                              ? currentAge >= d.start_age && currentAge <= d.end_age
                              : !!d.is_current;
                            return (
                              <PillarCardWrap
                                key={idx}
                                $selected={adminDaeunIdx === idx}
                                $current={isCurrent}
                                onClick={() => {
                                  setAdminDaeunIdx(prev => prev === idx ? null : idx);
                                  setAdminSaeunYear(null);
                                }}
                              >
                                <PillarTopLabel>
                                  {d.start_age != null ? `${d.start_age}세` : ""}
                                  {isCurrent && <div style={{ color: "#FF922B" }}>▶</div>}
                                </PillarTopLabel>
                                <PillarStarLabel>{tenStar}</PillarStarLabel>
                                <PillarGanBlock $oe={ganOe}>
                                  <PillarHangul $oe={ganOe}>{STEM_INFO[d.gan]?.hangul ?? d.gan}</PillarHangul>
                                  <PillarHanja $oe={ganOe}>{d.gan}</PillarHanja>
                                </PillarGanBlock>
                                <PillarGanBlock $oe={jiOe} style={{ marginTop: 2 }}>
                                  <PillarHangul $oe={jiOe}>{BRANCH_INFO[d.ji]?.hangul ?? d.ji}</PillarHangul>
                                  <PillarHanja $oe={jiOe}>{d.ji}</PillarHanja>
                                </PillarGanBlock>
                                <PillarStarLabel style={{ marginTop: 1 }}>{jiStar}</PillarStarLabel>
                                <PillarBottomLabel>{unju12}</PillarBottomLabel>
                              </PillarCardWrap>
                            );
                          })}
                        </PillarTimelineWrap>
                      </ResultCard>

                      {/* ── 2단: 선택된 대운 상세 ── */}
                      {adminDaeunIdx !== null && daeunScores[adminDaeunIdx] && (() => {
                        const d = daeunScores[adminDaeunIdx];
                        const dLabel: string = d.label || "";
                        const dIncluded = daeunSelected.length === 0
                          ? true
                          : daeunSelected.includes(dLabel);
                        const dayGan = palja?.day_pillar?.stem || "";
                        return (
                          <div style={{ margin: "12px 16px 0", opacity: dIncluded ? 1 : 0.45 }}>
                            <DaeunCard>
                              <DaeunHeader>
                                <DaeunGanJiBox $isYong={d.is_yong} $isGi={d.is_gi}>
                                  <span>{d.gan}</span>
                                  <span>{d.ji}</span>
                                </DaeunGanJiBox>
                                <DaeunMeta>
                                  <DaeunTitle>
                                    {d.age} {d.label}
                                    <DaeunYongGi $isYong={d.is_yong} $isGi={d.is_gi} style={{ marginLeft: 8 }}>
                                      {d.is_yong ? "용신대운" : d.is_gi ? "기신대운" : "보통대운"}
                                    </DaeunYongGi>
                                    {d.fortune_level && (
                                      <span style={{
                                        marginLeft: 6, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                        background: d.fortune_level === '대길' ? '#2F9E44' : d.fortune_level === '소길' ? '#74C0FC' : d.fortune_level === '소흉' ? '#FF922B' : d.fortune_level === '대흉' ? '#C92A2A' : '#868E96',
                                        color: '#fff',
                                      }}>{d.fortune_level}</span>
                                    )}
                                  </DaeunTitle>
                                  <DaeunSubtitle>
                                    {d.age_range} · 천간 {d.gan_oe}({d.gan}) · 지지 {d.ji_oe}({d.ji})
                                  </DaeunSubtitle>
                                </DaeunMeta>
                                <label style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",
                                  fontSize:11,fontWeight:700,padding:"5px 10px",borderRadius:16,
                                  border:"1.5px solid",flexShrink:0,
                                  borderColor:dIncluded?"#FF922B":"#495057",
                                  background:dIncluded?"rgba(255,146,43,0.12)":"transparent",
                                  color:dIncluded?"#FF922B":"#6C757D"}}>
                                  <input type="checkbox"
                                    checked={dIncluded}
                                    style={{display:"none"}}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setDaeunSelected(p => {
                                          if (p.length === 0) {
                                            return daeunScores.filter((_:any,i:number)=>i!==adminDaeunIdx).map((x:any)=>x.label).concat([dLabel]);
                                          }
                                          return [...p, dLabel];
                                        });
                                      } else {
                                        setDaeunSelected(p => {
                                          if (p.length === 0) {
                                            return daeunScores.filter((_:any,i:number)=>i!==adminDaeunIdx).map((x:any)=>x.label);
                                          }
                                          return p.filter(l => l !== dLabel);
                                        });
                                      }
                                    }}
                                  />
                                  {dIncluded ? "리포트 포함 ✓" : "포함 안함"}
                                </label>
                              </DaeunHeader>

                              <ScoreGrid>
                                {(["love","marry","business","career","health","wealth","family","friends"] as const).map(key => {
                                  const s = d[key]?.score ?? 0;
                                  return (
                                    <ScoreCell key={key} $score={s}>
                                      <ScoreLabel>{SCORE_LABELS[key]}</ScoreLabel>
                                      <ScoreNum $score={s}>{s}</ScoreNum>
                                      <ScoreBar $score={s} />
                                    </ScoreCell>
                                  );
                                })}
                              </ScoreGrid>

                              <ReasonList>
                                {(["love","marry","business","career","health","wealth","family","friends"] as const).map(key => {
                                  const reason = d[key]?.reason;
                                  if (!reason) return null;
                                  return (
                                    <ReasonRow key={key}>
                                      <ReasonKey>{SCORE_LABELS[key]}</ReasonKey>
                                      <ReasonText>{reason}</ReasonText>
                                    </ReasonRow>
                                  );
                                })}
                              </ReasonList>

                              {d.hap_chung_with_palja && Object.values(d.hap_chung_with_palja).some((v: any) => v?.length > 0) && (
                                <div style={{ marginTop: 10 }}>
                                  <SubLabel>이 대운의 형충파해</SubLabel>
                                  <TagRow>
                                    {Object.entries(d.hap_chung_with_palja).flatMap(([, arr]: [string, any]) =>
                                      (arr || []).map((s: string, i: number) => (
                                        <Tag key={`${s}-${i}`} $type={s.includes("합") ? "good" : "bad"}>{s}</Tag>
                                      ))
                                    )}
                                  </TagRow>
                                </div>
                              )}

                              {/* ── 리포트 표시 테마 체크박스 ── */}
                              {daeunEnabled && (() => {
                                const sa = d.start_age ?? parseInt(String(d.age||"0"));
                                const enabledThemes = daeunThemeConfig[dLabel] ?? getDefaultDaeunThemes(sa);
                                return (
                                  <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #E9ECEF"}}>
                                    <SubLabel>리포트 표시 항목 (이 대운)</SubLabel>
                                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                                      {ALL_DAEUN_THEMES.map(({key, label}) => {
                                        const checked = enabledThemes.includes(key);
                                        return (
                                          <label key={key} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",
                                            fontSize:12,padding:"4px 10px",borderRadius:20,border:"1.5px solid",
                                            borderColor:checked?"#7048E8":"#DEE2E6",
                                            background:checked?"rgba(112,72,232,0.08)":"#FAFAFA",
                                            color:checked?"#7048E8":"#ADB5BD",fontWeight:checked?700:400,
                                            transition:"all .15s"}}>
                                            <input type="checkbox" checked={checked} style={{display:"none"}}
                                              onChange={e => {
                                                const cur = daeunThemeConfig[dLabel] ?? getDefaultDaeunThemes(sa);
                                                const next = e.target.checked
                                                  ? [...cur.filter(t => t!==key), key]
                                                  : cur.filter(t => t!==key);
                                                setDaeunThemeConfig(p => ({...p, [dLabel]: next}));
                                              }}
                                            />
                                            {label}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* ── 연운 타임라인 ── */}
                              {d.saeun_scores && d.saeun_scores.length > 0 && (
                                <TimelineSection>
                                  <TimelineSectionTitle>
                                    연운 ({d.saeun_scores[0]?.year}~{d.saeun_scores[d.saeun_scores.length-1]?.year}) — 클릭하면 세부 점수 표시
                                  </TimelineSectionTitle>
                                  <PillarTimelineWrap>
                                    {d.saeun_scores.map((sy: any) => {
                                      const ganOe = STEM_OHAENG[sy.gan] || "토";
                                      const jiOe  = BRANCH_OHAENG[sy.ji] || "토";
                                      const tenStar = calcTenStar(dayGan, sy.gan);
                                      const jiStar  = calcTenStar(dayGan, sy.ji);
                                      const unju12  = (TWELVE_FORTUNE[dayGan] as Record<string,string>)?.[sy.ji] || "";
                                      const isCurYear = sy.year === new Date().getFullYear();
                                      return (
                                        <PillarCardWrap
                                          key={sy.year}
                                          $selected={adminSaeunYear === sy.year}
                                          $current={isCurYear}
                                          onClick={() => setAdminSaeunYear(prev => prev === sy.year ? null : sy.year)}
                                        >
                                          <PillarTopLabel>
                                            {sy.year}
                                            {isCurYear && <div style={{ color: "#339AF0" }}>▶</div>}
                                          </PillarTopLabel>
                                          <PillarStarLabel>{tenStar}</PillarStarLabel>
                                          <PillarGanBlock $oe={ganOe}>
                                            <PillarHangul $oe={ganOe}>{STEM_INFO[sy.gan]?.hangul ?? sy.gan}</PillarHangul>
                                            <PillarHanja $oe={ganOe}>{sy.gan}</PillarHanja>
                                          </PillarGanBlock>
                                          <PillarGanBlock $oe={jiOe} style={{ marginTop: 2 }}>
                                            <PillarHangul $oe={jiOe}>{BRANCH_INFO[sy.ji]?.hangul ?? sy.ji}</PillarHangul>
                                            <PillarHanja $oe={jiOe}>{sy.ji}</PillarHanja>
                                          </PillarGanBlock>
                                          <PillarStarLabel style={{ marginTop: 1 }}>{jiStar}</PillarStarLabel>
                                          <PillarBottomLabel>{unju12}</PillarBottomLabel>
                                        </PillarCardWrap>
                                      );
                                    })}
                                  </PillarTimelineWrap>

                                  {/* ── 선택된 연운 세부 + 월운 ── */}
                                  {adminSaeunYear !== null && (() => {
                                    const sy = d.saeun_scores.find((s: any) => s.year === adminSaeunYear);
                                    if (!sy) return null;
                                    return (
                                      <div style={{ marginTop: 10, background: "#fff", border: "1px solid var(--border-light)", borderRadius: 10, padding: "12px" }}>
                                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 8 }}>
                                          <div style={{ fontWeight: 700, fontSize: 13 }}>{sy.year_label}</div>
                                          <div style={{ display:"flex", gap: 8 }}>
                                            <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:11,color:"#7048E8"}}>
                                              <input type="checkbox"
                                                checked={saeunSelected.includes(sy.year)}
                                                onChange={e => setSaeunSelected(p => e.target.checked ? [...p,sy.year] : p.filter((y:number)=>y!==sy.year))}
                                                style={{width:13,height:13,accentColor:"#7048E8"}}
                                              />
                                              세운 리포트
                                            </label>
                                            {saeunSelected.includes(sy.year) && (
                                              <label style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:11,color:"#E64980"}}>
                                                <input type="checkbox"
                                                  checked={wolunSelected.includes(sy.year)}
                                                  onChange={e => setWolunSelected(p => e.target.checked ? [...p,sy.year] : p.filter((y:number)=>y!==sy.year))}
                                                  style={{width:13,height:13,accentColor:"#E64980"}}
                                                />
                                                월운 포함
                                              </label>
                                            )}
                                          </div>
                                        </div>
                                        <SaeunScoreRow>
                                          {(["love","business","career","health","wealth"] as const).map(k => (
                                            <SaeunScorePill key={k} $score={sy.scores[k]}>
                                              {SCORE_LABELS[k]} {sy.scores[k]}
                                            </SaeunScorePill>
                                          ))}
                                        </SaeunScoreRow>
                                        <SaeunEventList>
                                          {(sy.events || []).map((ev: string, ei: number) => (
                                            <div key={ei}>· {ev}</div>
                                          ))}
                                        </SaeunEventList>

                                        {/* ── 월운 타임라인 ── */}
                                        {sy.month_scores && sy.month_scores.length > 0 && (
                                          <TimelineSection>
                                            <TimelineSectionTitle>월운 — {adminSaeunYear}년 12개월</TimelineSectionTitle>
                                            <PillarTimelineWrap>
                                              {sy.month_scores.map((ms: any) => {
                                                const _GREG_M = [2,3,4,5,6,7,8,9,10,11,12,1];
                                                const gregMonth = ms.greg_month ?? _GREG_M[ms.month - 1];
                                                const ganOe = STEM_OHAENG[ms.gan] || "토";
                                                const jiOe  = BRANCH_OHAENG[ms.ji] || "토";
                                                const tenStar = calcTenStar(dayGan, ms.gan);
                                                const jiStar  = calcTenStar(dayGan, ms.ji);
                                                const unju12  = (TWELVE_FORTUNE[dayGan] as Record<string,string>)?.[ms.ji] || "";
                                                const mKey = `${sy.year}-${ms.month}`;
                                                const isSelM = adminMonthKey === mKey;
                                                return (
                                                  <PillarCardWrap
                                                    key={ms.month}
                                                    $selected={isSelM}
                                                    $current={false}
                                                    onClick={() => setAdminMonthKey(p => p === mKey ? null : mKey)}
                                                    style={{ cursor: 'pointer', outline: isSelM ? '2px solid #7048E8' : 'none' }}
                                                  >
                                                    <PillarTopLabel>{gregMonth}월</PillarTopLabel>
                                                    <PillarStarLabel>{tenStar}</PillarStarLabel>
                                                    <PillarGanBlock $oe={ganOe}>
                                                      <PillarHangul $oe={ganOe}>{STEM_INFO[ms.gan]?.hangul ?? ms.gan}</PillarHangul>
                                                      <PillarHanja $oe={ganOe}>{ms.gan}</PillarHanja>
                                                    </PillarGanBlock>
                                                    <PillarGanBlock $oe={jiOe} style={{ marginTop: 2 }}>
                                                      <PillarHangul $oe={jiOe}>{BRANCH_INFO[ms.ji]?.hangul ?? ms.ji}</PillarHangul>
                                                      <PillarHanja $oe={jiOe}>{ms.ji}</PillarHanja>
                                                    </PillarGanBlock>
                                                    <PillarStarLabel style={{ marginTop: 1 }}>{jiStar}</PillarStarLabel>
                                                    <PillarBottomLabel>{unju12}</PillarBottomLabel>
                                                  </PillarCardWrap>
                                                );
                                              })}
                                            </PillarTimelineWrap>

                                            {/* ── 월운 세부 + 일운 달력 (월운 카드 클릭 시) ── */}
                                            {adminMonthKey && adminMonthKey.startsWith(`${sy.year}-`) && (() => {
                                              const selSajuMonth = parseInt(adminMonthKey.split('-')[1]);
                                              const selMs = sy.month_scores.find((m: any) => m.month === selSajuMonth);
                                              if (!selMs) return null;

                                              // 입춘 기준 사주월 → 양력월 매핑
                                              const _GREG = [2,3,4,5,6,7,8,9,10,11,12,1];
                                              const calMonthRaw = selMs.greg_month ?? _GREG[selSajuMonth - 1];
                                              const calYear  = calMonthRaw === 1 && selSajuMonth === 12 ? sy.year + 1 : sy.year;
                                              const calMonth = calMonthRaw;
                                              const daysInMo = new Date(calYear, calMonth, 0).getDate();
                                              const firstDow = new Date(calYear, calMonth - 1, 1).getDay();

                                              const useGodStr  = detail?.use_god   || '';
                                              const avoidGodStr = detail?.avoid_god || '';
                                              const johuGodStr  = detail?.johu_god  || '';

                                              // 간지 → 오행 매핑
                                              const _OE: Record<string,string> = {
                                                ...STEM_OHAENG, ...BRANCH_OHAENG,
                                              };

                                              const dayScoreMap: Record<number, number> = {};
                                              const dayCatMap: Record<number, Record<string,number>> = {};
                                              (selMs.day_scores || []).forEach((ds: any) => {
                                                dayScoreMap[ds.d] = ds.s;
                                                dayCatMap[ds.d] = {love:ds.love??50,marry:ds.marry??50,business:ds.business??50,career:ds.career??50,health:ds.health??50,wealth:ds.wealth??50};
                                              });

                                              const dayItems = Array.from({ length: daysInMo }, (_, i) => {
                                                const d = i + 1;
                                                const gz = getDayGanzhi(calYear, calMonth, d);
                                                const gOe = _OE[gz.gan] || '';
                                                const jOe = _OE[gz.ji]  || '';
                                                const isYong = gOe === useGodStr || jOe === useGodStr;
                                                const isJohu = johuGodStr && (gOe === johuGodStr || jOe === johuGodStr);
                                                const isGi   = gOe === avoidGodStr || jOe === avoidGodStr;
                                                const kind   = isYong && !isGi ? 'yong'
                                                             : isJohu && !isGi ? 'johu'
                                                             : isGi && !isYong ? 'gi'
                                                             : 'neutral';
                                                const score = dayScoreMap[d] ?? null;
                                                return { d, gz, gOe, jOe, kind, score };
                                              });

                                              const weekLabels = ['일','월','화','수','목','금','토'];
                                              const kindStyle: Record<string, { bg: string; border: string }> = {
                                                yong:    { bg: '#EBFBEE', border: '#40C057' },
                                                johu:    { bg: '#FFF9DB', border: '#FAB005' },
                                                gi:      { bg: '#FFF5F5', border: '#FF6B6B' },
                                                neutral: { bg: '#F8F9FA', border: '#DEE2E6' },
                                              };

                                              return (
                                                <div style={{ marginTop: 16, borderRadius: 12, border: '1.5px solid #E64980aa', overflow: 'hidden' }}>
                                                  {/* ── 월운 점수 + 이벤트 ── */}
                                                  <div style={{ padding: '12px 14px', background: '#FFF0F6' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 800, color: '#C2255C', marginBottom: 8 }}>
                                                      {calYear}년 {calMonth}월 운세
                                                      <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                                                        ({selMs.gan}{selMs.ji}월)
                                                      </span>
                                                    </div>
                                                    <SaeunScoreRow>
                                                      {(['love','marry','business','career','health','wealth'] as const).map(k => (
                                                        <SaeunScorePill key={k} $score={selMs.scores?.[k] ?? 50}>
                                                          {SCORE_LABELS[k]} {selMs.scores?.[k] ?? '-'}
                                                        </SaeunScorePill>
                                                      ))}
                                                    </SaeunScoreRow>
                                                    {(selMs.events ?? []).length > 0 && (
                                                      <SaeunEventList style={{ marginTop: 8 }}>
                                                        {(selMs.events as string[]).map((ev: string, ei: number) => (
                                                          <div key={ei} style={{ color: ev.includes('주의') || ev.includes('이별') || ev.includes('손실') ? '#C92A2A' : ev.includes('강화') || ev.includes('호조') || ev.includes('유입') ? '#2F9E44' : '#495057' }}>
                                                            · {ev}
                                                          </div>
                                                        ))}
                                                      </SaeunEventList>
                                                    )}
                                                  </div>
                                                  {/* ── 일운 달력 ── */}
                                                  <div style={{ padding: '14px 16px', background: '#FAFAFF', borderTop: '1px solid #E64980aa' }}>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#7048E8', marginBottom: 10 }}>
                                                      일운 달력 — {calYear}년 {calMonth}월
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
                                                      {weekLabels.map(w => (
                                                        <div key={w} style={{ textAlign: 'center', fontSize: 10, color: '#888', padding: '3px 0', fontWeight: 700 }}>{w}</div>
                                                      ))}
                                                      {Array.from({ length: firstDow }, (_, i) => <div key={`ep-${i}`} />)}
                                                      {dayItems.map(({ d, gz, gOe, jOe, kind, score }) => {
                                                        const { bg, border } = kindStyle[kind];
                                                        const ganStar = calcTenStar(dayGan, gz.gan);
                                                        const jiStar  = calcTenStar(dayGan, gz.ji);
                                                        const scoreColor = score === null ? '#aaa' : score >= 65 ? '#2F9E44' : score >= 50 ? '#888' : '#C92A2A';
                                                        const cats = dayCatMap[d];
                                                        const CAT_LABELS: Record<string,string> = {love:'연',marry:'혼',business:'사',career:'직',health:'건',wealth:'재'};
                                                        return (
                                                          <div key={d} style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 6, padding: '4px 3px', textAlign: 'center', fontSize: 10, lineHeight: 1.3 }}>
                                                            <div style={{ fontWeight: 700, color: '#444', fontSize: 11 }}>{d}</div>
                                                            <div style={{ color: '#999', fontSize: 8 }}>{ganStar}</div>
                                                            <div style={{ color: OE_COLOR_A[gOe] || '#333' }}>{gz.gan}</div>
                                                            <div style={{ color: OE_COLOR_A[jOe] || '#333' }}>{gz.ji}</div>
                                                            <div style={{ color: '#999', fontSize: 8 }}>{jiStar}</div>
                                                            {score !== null && (
                                                              <div style={{ color: scoreColor, fontSize: 10, fontWeight: 700, marginTop: 1 }}>{score}</div>
                                                            )}
                                                            {cats && (
                                                              <div style={{ marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                                                                {(['love','marry','business','career','health','wealth'] as const).map(k => {
                                                                  const v = cats[k];
                                                                  const c = v >= 65 ? '#2F9E44' : v >= 50 ? '#888' : '#C92A2A';
                                                                  return <span key={k} style={{ fontSize: 9, color: c, lineHeight: 1.4, fontWeight: 600 }}>{CAT_LABELS[k]}{v}</span>;
                                                                })}
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                    <div style={{ marginTop: 8, display: 'flex', gap: 6, fontSize: 10, flexWrap: 'wrap' }}>
                                                      <span style={{ background: '#EBFBEE', border: '1px solid #40C057', borderRadius: 4, padding: '1px 6px' }}>● 용신일</span>
                                                      <span style={{ background: '#FFF9DB', border: '1px solid #FAB005', borderRadius: 4, padding: '1px 6px' }}>● 조후일</span>
                                                      <span style={{ background: '#FFF5F5', border: '1px solid #FF6B6B', borderRadius: 4, padding: '1px 6px' }}>● 기신일</span>
                                                      <span style={{ background: '#F8F9FA', border: '1px solid #DEE2E6', borderRadius: 4, padding: '1px 6px' }}>● 중립일</span>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })()}
                                          </TimelineSection>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </TimelineSection>
                              )}
                            </DaeunCard>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {/* ── 탭: 상대방 사주 ──────────────────────────── */}
              {tab === "partner" && (() => {
                const pi = consult.partner_info;
                const ps = partnerSaju;
                if (!pi) return (
                  <ResultCard>
                    <div style={{color:"var(--text-secondary)",fontSize:14,textAlign:"center",padding:"20px 0"}}>파트너 정보가 없습니다.</div>
                  </ResultCard>
                );
                return (
                  <div style={{paddingBottom:16}}>
                    {/* 파트너 기본 정보 */}
                    <ResultCard>
                      <CardTitle>상대방 기본 정보</CardTitle>
                      <InfoGrid>
                        <InfoChip><InfoLabel>이름</InfoLabel><InfoValue>{pi.name}</InfoValue></InfoChip>
                        <InfoChip><InfoLabel>성별</InfoLabel><InfoValue>{pi.gender==="female"?"여성":"남성"}</InfoValue></InfoChip>
                        <InfoChip>
                          <InfoLabel>생년월일</InfoLabel>
                          <InfoValue>{pi.birth_year}.{String(pi.birth_month||0).padStart(2,"0")}.{String(pi.birth_day||0).padStart(2,"0")} {fmtHour(pi.birth_hour, pi.birth_minute)}</InfoValue>
                        </InfoChip>
                        <InfoChip><InfoLabel>지역</InfoLabel><InfoValue>{pi.city||"서울"}</InfoValue></InfoChip>
                      </InfoGrid>
                      <div style={{display:"flex",gap:8,marginTop:12}}>
                        <Btn $variant="primary" style={{flex:1}} onClick={loadPartnerSaju} disabled={partnerLoading}>
                          {partnerLoading ? "분석 중..." : ps ? "사주 재분석" : "상대방 사주 분석"}
                        </Btn>
                        {scores && (
                          <Btn $variant="outline" style={{flex:1}} onClick={calcCompatScore} disabled={busy}>
                            {busy ? "계산 중..." : "궁합 점수 계산"}
                          </Btn>
                        )}
                      </div>
                    </ResultCard>

                    {ps && (() => {
                      const pp   = ps.saju_palja;
                      const wx   = ps.wuxing_analysis;
                      const dn   = ps.daeun || [];
                      const ds   = ps.daeun_scores || [];
                      const dayGan = pp?.day_pillar?.stem || "";
                      const dayJi  = pp?.day_pillar?.branch || "";
                      const dayOeP = STEM_OHAENG[dayGan] || "토";
                      const brInfoP = BRANCH_INFO[dayJi];
                      const STEM_COLOR_KR_P: Record<string,string> = {
                        甲:"청색",乙:"청색",丙:"적색",丁:"적색",戊:"황색",己:"황색",庚:"백색",辛:"백색",壬:"흑색",癸:"흑색"
                      };

                      const pCols = [
                        {key:"시",gan:pp?.hour_pillar?.stem||"",ji:pp?.hour_pillar?.branch||"",ganPk:"hour_gan",jiPk:"hour_ji"},
                        {key:"일",gan:pp?.day_pillar?.stem||"", ji:pp?.day_pillar?.branch||"", ganPk:"day_gan", jiPk:"day_ji"},
                        {key:"월",gan:pp?.month_pillar?.stem||"",ji:pp?.month_pillar?.branch||"",ganPk:"month_gan",jiPk:"month_ji"},
                        {key:"년",gan:pp?.year_pillar?.stem||"",ji:pp?.year_pillar?.branch||"",ganPk:"year_gan",jiPk:"year_ji"},
                      ];
                      const pYearGroup = getYearBranchGroup(pp?.year_pillar?.branch||"");
                      const pSinsalPP: Record<string,string[]> = ps.ai_scores_detail?.sinsal?.per_pillar || {};
                      const pGm = calcGongmang(dayGan, dayJi);
                      const pColorKr = STEM_COLOR_KR_P[dayGan] || "";
                      const labelTd: React.CSSProperties = {
                        width:38,textAlign:"center",padding:"4px 2px",
                        background:"#F0F0F0",border:"1px solid #E9ECEF",
                        fontSize:12,color:"#444444",fontWeight:700,verticalAlign:"middle",
                      };
                      const dataTd=(isDay:boolean): React.CSSProperties=>({
                        textAlign:"center",padding:"7px 4px",
                        background:isDay?"rgba(26,26,46,0.04)":"#fff",
                        border:"1px solid #E9ECEF",verticalAlign:"middle",color:"#222222",
                      });
                      const smallTd=(isDay:boolean): React.CSSProperties=>({
                        textAlign:"center",padding:"5px 4px",
                        background:isDay?"rgba(26,26,46,0.04)":"#FAFAFA",
                        border:"1px solid #E9ECEF",verticalAlign:"middle",color:"#222222",
                      });

                      return (
                        <>
                          {/* 일주 + 사주팔자 (전체) */}
                          <ResultCard>
                            <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:20,padding:"12px 14px",background:"#F8F9FA",borderRadius:12}}>
                              <div style={{fontSize:36,lineHeight:1,flexShrink:0}}>{brInfoP?.animalEmoji||"☯"}</div>
                              <div style={{flex:1}}>
                                <div style={{fontSize:16,fontWeight:800,color:"var(--text-primary)",marginBottom:6}}>
                                  {dayGan}{dayJi} 일주
                                  <span style={{fontWeight:500,color:"var(--text-secondary)",fontSize:13,marginLeft:6}}>({pColorKr} {brInfoP?.animalName||""})</span>
                                </div>
                                <div style={{fontSize:12,color:"var(--text-secondary)"}}>
                                  {pi.birth_year}.{String(pi.birth_month||0).padStart(2,"0")}.{String(pi.birth_day||0).padStart(2,"0")} {fmtHour(pi.birth_hour, pi.birth_minute)} · {pi.gender==="female"?"여자":"남자"}
                                </div>
                              </div>
                            </div>
                            <CardTitle>{ps.name} 사주팔자</CardTitle>
                            <div style={{overflowX:"auto"}}>
                              <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed",minWidth:260}}>
                                <thead>
                                  <tr>
                                    <td style={{width:38,background:"#F8F9FA",border:"1px solid #E9ECEF",borderBottom:"2px solid #E9ECEF"}}/>
                                    {pCols.map((col,ci)=>(
                                      <td key={ci} style={{textAlign:"center",padding:"8px 4px",background:ci===1?"#1A1A2E":"#F0F0F0",color:ci===1?"#fff":"#444444",fontSize:12,fontWeight:700,border:"1px solid #E9ECEF",borderBottom:"2px solid #E9ECEF"}}>
                                        {col.key}주
                                      </td>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* 천간 */}
                                  <tr>
                                    <td style={labelTd}>천간</td>
                                    {pCols.map((col,ci)=>{
                                      const oe=STEM_OHAENG[col.gan]||"토";
                                      const st=OHAENG_STYLE[oe];
                                      const info=STEM_INFO[col.gan];
                                      const yy=info?.yinYang?"+":"−";
                                      return (
                                        <td key={ci} style={{...dataTd(ci===1),position:"relative",paddingBottom:20,paddingTop:8}}>
                                          <div style={{fontSize:15,color:st?.color||"#333",marginBottom:2,fontWeight:600}}>{info?.hangul}</div>
                                          <div style={{fontSize:28,fontWeight:800,lineHeight:1,color:st?.color||"#333"}}>{col.gan}</div>
                                          <div style={{position:"absolute",bottom:4,right:5,fontSize:15,fontWeight:700,color:st?.color||"#333"}}>{yy}{oe}</div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 십성(천간) */}
                                  <tr>
                                    <td style={labelTd}>십성</td>
                                    {pCols.map((col,ci)=>{
                                      const star=calcTenStar(dayGan,col.gan);
                                      const oe=STEM_OHAENG[col.gan]||"토";
                                      return (
                                        <td key={ci} style={dataTd(ci===1)}>
                                          <span style={{fontSize:15,fontWeight:700,color:OHAENG_STYLE[oe]?.color||"#333"}}>{star}</span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 신살·길성(천간) - 파트너 */}
                                  <tr>
                                    <td style={{...labelTd,fontSize:10}}>신살</td>
                                    {pCols.map((col,ci)=>{
                                      const ganSinsal:string[]=pSinsalPP[col.ganPk]||[];
                                      return (
                                        <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}>
                                          {ganSinsal.length>0 ? (
                                            <div style={{display:"flex",flexWrap:"wrap",gap:2,justifyContent:"center"}}>
                                              {ganSinsal.map((s:string,si:number)=>{
                                                const isGil=["귀인","귀학","암록","복성","학당","금여","월덕","천덕"].some(w=>s.includes(w));
                                                return <span key={si} style={{fontSize:15,fontWeight:700,color:isGil?"#2F9E44":"#C92A2A"}}>{s}</span>;
                                              })}
                                            </div>
                                          ) : <span style={{fontSize:11,color:"#ccc"}}>—</span>}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 지지 */}
                                  <tr>
                                    <td style={labelTd}>지지</td>
                                    {pCols.map((col,ci)=>{
                                      const br=BRANCH_INFO[col.ji];
                                      const oe=br?.ohaeng||BRANCH_OHAENG[col.ji]||"토";
                                      const st=OHAENG_STYLE[oe];
                                      const bonggi=br?.jijanggan?.[br.jijanggan.length-1]??"";
                                      const yy=(STEM_INFO[bonggi]?.yinYang??br?.yinYang)?"+":"−";
                                      return (
                                        <td key={ci} style={{...dataTd(ci===1),position:"relative",paddingBottom:20,paddingTop:8}}>
                                          <div style={{fontSize:15,color:st?.color||"#333",marginBottom:2,fontWeight:600}}>{br?.hangul}</div>
                                          <div style={{fontSize:28,fontWeight:800,lineHeight:1,color:st?.color||"#333"}}>{col.ji}</div>
                                          <div style={{position:"absolute",bottom:4,right:5,fontSize:15,fontWeight:700,color:st?.color||"#333"}}>{yy}{oe}</div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 십성(지지) */}
                                  <tr>
                                    <td style={labelTd}>십성</td>
                                    {pCols.map((col,ci)=>{
                                      const star=calcTenStar(dayGan,col.ji);
                                      const oe=BRANCH_INFO[col.ji]?.ohaeng||BRANCH_OHAENG[col.ji]||"토";
                                      return (
                                        <td key={ci} style={dataTd(ci===1)}>
                                          <span style={{fontSize:15,fontWeight:700,color:OHAENG_STYLE[oe]?.color||"#333"}}>{star}</span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 신살·길성(지지) - 파트너 */}
                                  <tr>
                                    <td style={{...labelTd,fontSize:10}}>신살</td>
                                    {pCols.map((col,ci)=>{
                                      const jiSinsal:string[]=pSinsalPP[col.jiPk]||[];
                                      return (
                                        <td key={ci} style={{...smallTd(ci===1),padding:"3px 2px"}}>
                                          {jiSinsal.length>0 ? (
                                            <div style={{display:"flex",flexWrap:"wrap",gap:2,justifyContent:"center"}}>
                                              {jiSinsal.map((s:string,si:number)=>{
                                                const isGil=["귀인","귀학","암록","복성","학당","금여","월덕","천덕","반안살","장성살"].some(w=>s.includes(w)||s===w);
                                                return <span key={si} style={{fontSize:15,fontWeight:700,color:isGil?"#2F9E44":"#C92A2A"}}>{s}</span>;
                                              })}
                                            </div>
                                          ) : <span style={{fontSize:11,color:"#ccc"}}>—</span>}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 지장간 */}
                                  <tr>
                                    <td style={labelTd}>지장간</td>
                                    {pCols.map((col,ci)=>{
                                      const jjg=BRANCH_INFO[col.ji]?.jijanggan||[];
                                      return (
                                        <td key={ci} style={smallTd(ci===1)}>
                                          <span style={{fontSize:15,letterSpacing:1,fontWeight:600}}>
                                            {jjg.map((c:string,jIdx:number)=>(
                                              <span key={jIdx} style={{color:OHAENG_STYLE[STEM_OHAENG[c]||"토"]?.color||"#333",marginRight:jIdx<jjg.length-1?4:0}}>{c}</span>
                                            ))}
                                          </span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                  {/* 12운성 */}
                                  <tr>
                                    <td style={labelTd}>12운성</td>
                                    {pCols.map((col,ci)=>(
                                      <td key={ci} style={smallTd(ci===1)}>
                                        <span style={{fontSize:15,color:"#222222",fontWeight:600}}>{TWELVE_FORTUNE[dayGan]?.[col.ji]||"—"}</span>
                                      </td>
                                    ))}
                                  </tr>
                                  {/* 12신살 */}
                                  <tr>
                                    <td style={labelTd}>12신살</td>
                                    {pCols.map((col,ci)=>(
                                      <td key={ci} style={smallTd(ci===1)}>
                                        <span style={{fontSize:15,color:"#222222",fontWeight:600}}>{TWELVE_SPIRIT[pYearGroup]?.[col.ji]||"—"}</span>
                                      </td>
                                    ))}
                                  </tr>
                                  {/* 공망 */}
                                  <tr>
                                    <td style={labelTd}>공망</td>
                                    {pCols.map((col,ci)=>{
                                      const isGm=pGm.includes(col.ji);
                                      return (
                                        <td key={ci} style={smallTd(ci===1)}>
                                          <span style={{fontSize:15,fontWeight:isGm?700:600,color:isGm?"#C92A2A":"#222222"}}>{isGm?"공망":"—"}</span>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            {/* 공망 안내 */}
                            {(() => {
                              const gmInChart=pCols.filter(c=>pGm.includes(c.ji)).map(c=>JI_KOR_MAP[c.ji]||c.ji);
                              return (
                                <div style={{marginTop:10,padding:"8px 12px",background:"#FFF5F5",borderRadius:8,fontSize:12,color:"#C92A2A",border:"1px solid #FFCCCC"}}>
                                  <strong>공망(空亡)</strong>: {JI_KOR_MAP[pGm[0]]||pGm[0]}·{JI_KOR_MAP[pGm[1]]||pGm[1]}
                                  {gmInChart.length>0?` — 원국 ${gmInChart.join("·")}지가 공망에 해당, 해당 주의 기운이 약해집니다.`:" — 원국에 공망 지지 없음"}
                                </div>
                              );
                            })()}
                          </ResultCard>

                          {/* 신살·길성 (ps.ai_scores_detail 있을 때) */}
                          {ps.ai_scores_detail?.sinsal && (() => {
                            const pss = ps.ai_scores_detail.sinsal;
                            const present: any[] = pss.present || [];
                            const gilItems = present.filter((x:any)=>x.type==="gil");
                            const hyungItems = present.filter((x:any)=>x.type==="hyung");
                            const neutralItems = present.filter((x:any)=>x.type==="neutral");
                            const gongmangP: string[] = pss.gongmang || [];
                            const groupP: string = pss.group || "";
                            const PSinsalBadge = ({item}:{item:any})=>{
                              const isGil=item.type==="gil"; const isHy=item.type==="hyung";
                              const bg=isGil?"#EBFBEE":isHy?"#FFF0F0":"#FFF9DB";
                              const col=isGil?"#2F9E44":isHy?"#C92A2A":"#E67700";
                              return (
                                <div style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:8,background:bg,border:`1px solid ${col}33`,marginBottom:4,marginRight:4}}>
                                  <span style={{fontSize:16,fontWeight:800,color:col}}>{item.char}</span>
                                  <div style={{lineHeight:1.2}}>
                                    <div style={{fontSize:12,fontWeight:700,color:col}}>{item.name}</div>
                                    <div style={{fontSize:9,color:"#868E96"}}>{item.pos}</div>
                                  </div>
                                </div>
                              );
                            };
                            return (
                              <ResultCard>
                                <CardTitle>신살 · 길성 ({ps.name})</CardTitle>
                                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:12}}>
                                  {groupP && <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:"#F3F0FF",color:"#6741D9",border:"1.5px solid #9775FA44"}}>삼합국: {groupP}</span>}
                                  {gongmangP.length>0 && <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"#FFF9DB",color:"#E67700",border:"1.5px solid #FFD43B55"}}>공망: {gongmangP.join("·")}</span>}
                                </div>
                                {gilItems.length>0 && (
                                  <div style={{marginBottom:12}}>
                                    <div style={{fontSize:11,fontWeight:700,color:"#2F9E44",marginBottom:6}}>길신 (吉神) {gilItems.length}개</div>
                                    <div style={{display:"flex",flexWrap:"wrap"}}>{gilItems.map((item:any,i:number)=><PSinsalBadge key={i} item={item}/>)}</div>
                                  </div>
                                )}
                                {hyungItems.length>0 && (
                                  <div style={{marginBottom:12}}>
                                    <div style={{fontSize:11,fontWeight:700,color:"#C92A2A",marginBottom:6}}>흉살 (凶殺) {hyungItems.length}개</div>
                                    <div style={{display:"flex",flexWrap:"wrap"}}>{hyungItems.map((item:any,i:number)=><PSinsalBadge key={i} item={item}/>)}</div>
                                  </div>
                                )}
                                {neutralItems.length>0 && (
                                  <div>
                                    <div style={{fontSize:11,fontWeight:700,color:"#E67700",marginBottom:6}}>중성신살 {neutralItems.length}개</div>
                                    <div style={{display:"flex",flexWrap:"wrap"}}>{neutralItems.map((item:any,i:number)=><PSinsalBadge key={i} item={item}/>)}</div>
                                  </div>
                                )}
                                {present.length===0 && <div style={{fontSize:13,color:"var(--text-secondary)",textAlign:"center",padding:"16px 0"}}>해당 신살 없음</div>}
                              </ResultCard>
                            );
                          })()}

                          {/* 오행 분석 — 오각형 다이어그램 */}
                          {wx && (() => {
                            type DmKey = "opt_off_off"|"opt_on_off"|"opt_off_on"|"opt_on_on";
                            const dmKeyP: DmKey = `opt_${applyHapP?"on":"off"}_${applyWeightP?"on":"off"}` as DmKey;
                            const wxExtP = wx?.extended_analysis as any;
                            const dmP = wxExtP?.diagram_matrix as any;
                            const pctP = dmP?.[dmKeyP]?.pct as Record<string,number>|undefined;
                            return (
                              <ResultCard>
                                <CardTitle>{ps.name} 오행 분석</CardTitle>
                                {/* 토글 */}
                                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12,padding:"10px 12px",background:"#F8F9FA",borderRadius:10}}>
                                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                                    <input type="checkbox" checked={applyHapP} onChange={()=>setApplyHapP(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                                    합에 따른 오행변화 적용
                                    <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyHapP?"#EBFBEE":"#F1F3F5",color:applyHapP?"#2F9E44":"#ADB5BD"}}>{applyHapP?"적용중":"미적용"}</span>
                                  </label>
                                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                                    <input type="checkbox" checked={applyWeightP} onChange={()=>setApplyWeightP(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                                    조후와 궁성 보정값 적용
                                    <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyWeightP?"#EBFBEE":"#F1F3F5",color:applyWeightP?"#2F9E44":"#ADB5BD"}}>{applyWeightP?"적용중":"미적용"}</span>
                                  </label>
                                </div>
                                <div style={{display:"flex",gap:16,fontSize:11,marginBottom:4}}>
                                  <span style={{color:"#5B9BD5"}}>→ 생(生)</span>
                                  <span style={{color:"#D05050"}}>→ 극(剋)</span>
                                </div>
                                <div style={{maxWidth:260,margin:"0 auto"}}>
                                  <OePentagonAdmin wx={wx} dayOe={dayOeP} uid="pt" pct={pctP}/>
                                </div>
                                {(wx.use_god||wx.avoid_god) && (
                                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:12}}>
                                    {wx.strength && <Tag $type="neutral">{wx.strength==="strong"?"신강":"신약"}</Tag>}
                                    {wx.use_god   && <Tag $type="good">용신: {wx.use_god}</Tag>}
                                    {wx.avoid_god && <Tag $type="bad">기신: {wx.avoid_god}</Tag>}
                                  </div>
                                )}
                              </ResultCard>
                            );
                          })()}

                          {/* 오행 나란히 비교 — 내담자 ↔ 상대방 */}
                          {wuxing && wx && (() => {
                            const myDayGanX = palja?.day_pillar?.stem || "";
                            const myDayOeX  = STEM_OHAENG[myDayGanX] || "토";
                            const oc = consult.report_config?.ohaeng_compat;
                            type DmKey = "opt_off_off"|"opt_on_off"|"opt_off_on"|"opt_on_on";
                            const cmpKey: DmKey = `opt_${applyHapCmp?"on":"off"}_${applyWeightCmp?"on":"off"}` as DmKey;
                            const myDmCmp = (wuxing?.extended_analysis as any)?.diagram_matrix?.[cmpKey]?.pct as Record<string,number>|undefined;
                            const ptDmCmp = (wx?.extended_analysis as any)?.diagram_matrix?.[cmpKey]?.pct as Record<string,number>|undefined;
                            return (
                              <ResultCard>
                                <CardTitle>오행 비교 <span style={{fontSize:11,color:"#ADB5BD",fontWeight:400}}>내담자 ↔ 상대방</span></CardTitle>
                                {/* 토글 */}
                                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12,padding:"10px 12px",background:"#F8F9FA",borderRadius:10}}>
                                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                                    <input type="checkbox" checked={applyHapCmp} onChange={()=>setApplyHapCmp(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                                    합에 따른 오행변화 적용
                                    <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyHapCmp?"#EBFBEE":"#F1F3F5",color:applyHapCmp?"#2F9E44":"#ADB5BD"}}>{applyHapCmp?"적용중":"미적용"}</span>
                                  </label>
                                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                                    <input type="checkbox" checked={applyWeightCmp} onChange={()=>setApplyWeightCmp(v=>!v)} style={{width:16,height:16,cursor:"pointer"}}/>
                                    조후와 궁성 보정값 적용
                                    <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10,background:applyWeightCmp?"#EBFBEE":"#F1F3F5",color:applyWeightCmp?"#2F9E44":"#ADB5BD"}}>{applyWeightCmp?"적용중":"미적용"}</span>
                                  </label>
                                </div>
                                <div style={{display:"flex",gap:16,fontSize:12,marginBottom:10}}>
                                  <span style={{color:"#5B9BD5"}}>→ 생(生)</span>
                                  <span style={{color:"#D05050"}}>→ 극(剋)</span>
                                  {oc?.total_score != null && (
                                    <span style={{fontWeight:700,color:oc.total_score>=70?"#2F9E44":oc.total_score>=50?"#FF922B":"#E03131"}}>
                                      오행 궁합 {oc.total_score}점
                                    </span>
                                  )}
                                </div>
                                <div style={{display:"flex",gap:8}}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:12,fontWeight:700,color:OE_COLOR_A[myDayOeX],textAlign:"center",marginBottom:4}}>{consult.name}</div>
                                    <OePentagonAdmin wx={wuxing} dayOe={myDayOeX} uid="me2" pct={myDmCmp}/>
                                  </div>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:12,fontWeight:700,color:OE_COLOR_A[dayOeP],textAlign:"center",marginBottom:4}}>{pi.name}</div>
                                    <OePentagonAdmin wx={wx} dayOe={dayOeP} uid="pt2" pct={ptDmCmp}/>
                                  </div>
                                </div>
                                {oc?.interpretation && (
                                  <div style={{marginTop:12,padding:"10px 12px",background:"#F0F8FF",border:"1px solid #A5D8FF",borderRadius:10,fontSize:13,lineHeight:1.7,color:"#1864AB"}}>
                                    {oc.interpretation}
                                  </div>
                                )}
                              </ResultCard>
                            );
                          })()}

                          {/* 대운별 운세 (fortune scores 있을 때) */}
                          {ds.length > 0 && (
                            <div style={{padding:"0 0 12px"}}>
                              <div style={{padding:"12px 16px 6px",fontSize:13,fontWeight:700,color:"var(--text-primary)"}}>
                                {ps.name} 대운별 운세
                              </div>
                              {ds.map((d:any,idx:number)=>(
                                <div key={idx} style={{margin:"12px 16px 0"}}>
                                  <DaeunCard>
                                    <DaeunHeader>
                                      <DaeunGanJiBox $isYong={d.is_yong} $isGi={d.is_gi}>
                                        <span>{d.gan}</span>
                                        <span>{d.ji}</span>
                                      </DaeunGanJiBox>
                                      <DaeunMeta>
                                        <DaeunTitle>
                                          {d.age} {d.label}
                                          <DaeunYongGi $isYong={d.is_yong} $isGi={d.is_gi} style={{marginLeft:8}}>
                                            {d.is_yong?"용신대운":d.is_gi?"기신대운":"보통대운"}
                                          </DaeunYongGi>
                                        </DaeunTitle>
                                        <DaeunSubtitle>{d.age_range} · 천간 {d.gan_oe}({d.gan}) · 지지 {d.ji_oe}({d.ji})</DaeunSubtitle>
                                      </DaeunMeta>
                                    </DaeunHeader>
                                    <ScoreGrid>
                                      {(["love","marry","business","career","health","wealth","family","friends"] as const).map(key=>{
                                        const s=d[key]?.score??0;
                                        return(
                                          <ScoreCell key={key} $score={s}>
                                            <ScoreLabel>{SCORE_LABELS[key]}</ScoreLabel>
                                            <ScoreNum $score={s}>{s}</ScoreNum>
                                            <ScoreBar $score={s}/>
                                          </ScoreCell>
                                        );
                                      })}
                                    </ScoreGrid>
                                    <ReasonList>
                                      {(["love","marry","business","career","health","wealth","family","friends"] as const).map(key=>{
                                        const reason=d[key]?.reason;
                                        if(!reason)return null;
                                        return(
                                          <ReasonRow key={key}>
                                            <ReasonKey>{SCORE_LABELS[key]}</ReasonKey>
                                            <ReasonText>{reason}</ReasonText>
                                          </ReasonRow>
                                        );
                                      })}
                                    </ReasonList>
                                    {d.saeun_scores && d.saeun_scores.length > 0 && (
                                      <>
                                        <SaeunToggle onClick={()=>setOpenSaeun(p=>({...p,[`p${idx}`]:!p[`p${idx}`]}))}>
                                          <span>세운별 운세 ({d.saeun_scores[0]?.year}~{d.saeun_scores[d.saeun_scores.length-1]?.year})</span>
                                          <span>{openSaeun[`p${idx}`]?"▲ 접기":"▼ 펼치기"}</span>
                                        </SaeunToggle>
                                        {openSaeun[`p${idx}`] && (
                                          <SaeunGrid>
                                            {d.saeun_scores.map((sy:any)=>(
                                              <SaeunCard key={sy.year} $isYong={sy.is_yong&&!sy.is_gi} $isGi={sy.is_gi&&!sy.is_yong}>
                                                <SaeunYearLabel>{sy.year_label}</SaeunYearLabel>
                                                <SaeunScoreRow>
                                                  {(["love","business","career","health","wealth"] as const).map(k=>(
                                                    <SaeunScorePill key={k} $score={sy.scores[k]}>
                                                      {SCORE_LABELS[k]} {sy.scores[k]}
                                                    </SaeunScorePill>
                                                  ))}
                                                </SaeunScoreRow>
                                                <SaeunEventList>
                                                  {(sy.events||[]).map((ev:string,ei:number)=>(
                                                    <div key={ei}>· {ev}</div>
                                                  ))}
                                                </SaeunEventList>
                                              </SaeunCard>
                                            ))}
                                          </SaeunGrid>
                                        )}
                                      </>
                                    )}
                                  </DaeunCard>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* fortune score 없고 기본 대운만 있을 때 */}
                          {ds.length===0 && dn.length>0 && (
                            <ResultCard>
                              <CardTitle>{ps.name} 대운 흐름</CardTitle>
                              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                                {dn.map((d:any,i:number)=>{
                                  const gOe=d.gan_wuxing||STEM_OHAENG[d.gan]||"토";
                                  return(
                                    <div key={i} style={{display:"flex",gap:12,alignItems:"center",
                                      padding:"10px 12px",background:"#F8F9FA",borderRadius:10,border:"1px solid #E9ECEF"}}>
                                      <div style={{fontSize:20,fontWeight:900,color:OE_COLOR_A[gOe]||"#555",minWidth:44,textAlign:"center"}}>
                                        {d.gan}{d.ji}
                                      </div>
                                      <div style={{flex:1}}>
                                        <div style={{fontSize:12,fontWeight:700}}>{d.start_age}~{d.end_age}세</div>
                                        <div style={{fontSize:11,color:"var(--text-secondary)",marginTop:2}}>
                                          천간 {gOe} · 지지 {d.ji_wuxing||BRANCH_OHAENG[d.ji]||"?"}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </ResultCard>
                          )}

                          {/* 오행 궁합 점수 */}
                          {consult.report_config?.ohaeng_compat ? (() => {
                            const oc = consult.report_config.ohaeng_compat;
                            const s = oc.total_score;
                            return (
                              <ResultCard>
                                <CardTitle>오행 궁합 점수 (한난조습)</CardTitle>
                                <div style={{textAlign:"center",marginBottom:16}}>
                                  <div style={{fontSize:56,fontWeight:900,color:s>=70?"#2F9E44":s>=50?"#FF922B":"#E03131",lineHeight:1}}>{s}</div>
                                  <div style={{fontSize:13,color:"var(--text-secondary)",marginTop:4}}>/ 100점</div>
                                </div>
                                <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                                  {Object.entries(oc.breakdown||{}).map(([k,v]:any)=>(
                                    <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"7px 10px",background:"#F8F9FA",borderRadius:8}}>
                                      <span style={{color:"var(--text-secondary)"}}>{k}</span>
                                      <span style={{fontWeight:700,color:Number(v)>=0?"#2F9E44":"#E03131"}}>{Number(v)>=0?"+":""}{v}</span>
                                    </div>
                                  ))}
                                </div>
                                {oc.interpretation && (
                                  <div style={{padding:"12px",background:"#F0F8FF",border:"1px solid #A5D8FF",borderRadius:10,fontSize:13,lineHeight:1.7,color:"#1864AB"}}>
                                    {oc.interpretation}
                                  </div>
                                )}
                                <Btn $variant="outline" style={{marginTop:12,width:"100%"}} onClick={calcCompatScore} disabled={busy}>
                                  {busy?"계산 중...":"점수 재계산"}
                                </Btn>
                              </ResultCard>
                            );
                          })() : (
                            <div style={{padding:"0 16px"}}>
                              <Btn $variant="primary" style={{width:"100%"}} onClick={calcCompatScore} disabled={busy||!scores}>
                                {busy?"계산 중...":"오행 궁합 점수 계산"}
                              </Btn>
                              {!scores && <div style={{fontSize:12,color:"#ADB5BD",marginTop:6,textAlign:"center"}}>먼저 사주 엔진 분석을 실행하세요.</div>}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* ── 탭: 리포트 구성 ──────────────────────────── */}
              {tab === "report" && (() => {
                const svc = consult.service_type || "종합운세";
                const isCompat = consult.partner_info != null;
                const isDeep   = svc === "심층궁합";
                const isNewYear = svc === "신년운세";
                return (
                  <div style={{padding:"0 0 16px"}}>
                    {/* ── 기간 운세 포함 여부 ── */}
                    {!isCompat && (
                      <ResultCard>
                        <CardTitle>기간 운세 포함 여부</CardTitle>
                        <div style={{fontSize:12,color:"#ADB5BD",marginBottom:12,lineHeight:1.6}}>
                          대운·세운·월운 포함 여부를 설정합니다.<br/>
                          대운별 포함/제외 및 항목(연애·직업 등)은 <strong style={{color:"#FF922B"}}>대운별운세 탭</strong>에서 각 대운 카드에서 제어하세요.
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:14}}>
                            <input type="checkbox" checked={daeunEnabled}
                              onChange={e => setDaeunEnabled(e.target.checked)}
                              style={{width:16,height:16,accentColor:"#FF922B",cursor:"pointer"}} />
                            <span style={{fontWeight:daeunEnabled?700:400,color:daeunEnabled?"var(--text-primary)":"#ADB5BD"}}>
                              대운 (10년 주기)
                            </span>
                          </label>
                          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:14}}>
                            <input type="checkbox" checked={saeunEnabled}
                              onChange={e => setSaeunEnabled(e.target.checked)}
                              style={{width:16,height:16,accentColor:"#FF922B",cursor:"pointer"}} />
                            <span style={{fontWeight:saeunEnabled?700:400,color:saeunEnabled?"var(--text-primary)":"#ADB5BD"}}>
                              세운 (연도별) — 대운별운세 탭에서 연도 옆 '세운 리포트' 체크
                            </span>
                          </label>
                          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:14}}>
                            <input type="checkbox" checked={wolunEnabled}
                              onChange={e => setWolunEnabled(e.target.checked)}
                              style={{width:16,height:16,accentColor:"#FF922B",cursor:"pointer"}} />
                            <span style={{fontWeight:wolunEnabled?700:400,color:wolunEnabled?"var(--text-primary)":"#ADB5BD"}}>
                              월운 (1~12월){isNewYear?" — 신년운세 포함 권장":""} — 세운 체크 후 '월운 포함' 추가 선택
                            </span>
                          </label>
                        </div>
                        {(saeunSelected.length > 0 || wolunSelected.length > 0) && (
                          <div style={{marginTop:10,padding:"8px 12px",background:"rgba(255,146,43,0.06)",borderRadius:10,fontSize:12,color:"#FF922B"}}>
                            세운 선택: {[...saeunSelected].sort((a,b)=>a-b).join(", ")}년
                            {wolunSelected.length > 0 && <> | 월운: {[...wolunSelected].sort((a,b)=>a-b).join(", ")}년</>}
                          </div>
                        )}
                      </ResultCard>
                    )}

                    {/* ── 궁합 항목 체크 + 분석 입력 ── */}
                    {isCompat && (
                      <ResultCard>
                        <CardTitle>{isDeep ? "💍 심층 궁합" : "💕 연애 궁합"} 항목별 분석</CardTitle>
                        <div style={{display:"flex",flexDirection:"column",gap:16}}>
                          {Object.keys(DEFAULT_COMPAT_ITEMS).map(key => {
                            const isDeepOnly = ["결혼운/가치관","자녀운","이별수/위험시기"].includes(key);
                            const disabled = isDeepOnly && !isDeep;
                            const checked = compatItems[key] ?? DEFAULT_COMPAT_ITEMS[key];
                            return (
                              <div key={key} style={{opacity:disabled?0.35:1}}>
                                <label style={{display:"flex",alignItems:"center",gap:10,cursor:disabled?"not-allowed":"pointer",marginBottom:checked&&!disabled?8:0}}>
                                  <input type="checkbox"
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={e => setCompatItems(p => ({...p,[key]:e.target.checked}))}
                                    style={{width:16,height:16,accentColor:"#E64980",cursor:disabled?"not-allowed":"pointer"}} />
                                  <span style={{fontWeight:checked?700:400,color:checked?"var(--text-primary)":"#ADB5BD",fontSize:14}}>
                                    {key}
                                    {isDeepOnly && <span style={{fontSize:10,color:"#E64980",marginLeft:6}}>심층전용</span>}
                                  </span>
                                </label>
                                {checked && !disabled && (
                                  <div style={{marginLeft:26,display:"flex",gap:8}}>
                                    <input
                                      type="number" min={0} max={100} placeholder="점수"
                                      value={compatAnalysis[key]?.score ?? ""}
                                      onChange={e => setCompatAnalysis(p => ({...p,[key]:{...p[key],score:e.target.value}}))}
                                      style={{width:70,padding:"7px 10px",border:"1.5px solid #DEE2E6",borderRadius:8,fontSize:13,fontFamily:"inherit"}}
                                    />
                                    <Textarea
                                      value={compatAnalysis[key]?.text ?? ""}
                                      onChange={e => setCompatAnalysis(p => ({...p,[key]:{...p[key],text:e.target.value}}))}
                                      placeholder={`${key} 분석 내용 입력...`}
                                      style={{flex:1,minHeight:60,fontSize:13}}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </ResultCard>
                    )}

                    {/* ── 궁합 추가 분석 (연애궁합/심층궁합 공통) ── */}
                    {isCompat && (
                      <ResultCard>
                        <CardTitle>💕 만남·이상형·연애성향 분석</CardTitle>

                        <SubLabel>만날 가능성이 높은 시기</SubLabel>
                        <Textarea
                          value={meetingTime}
                          onChange={e => setMeetingTime(e.target.value)}
                          placeholder="예: 2023~2025년 사이, 직장 또는 지인 소개로 만날 가능성 높음"
                          style={{minHeight:50,marginBottom:14}}
                        />

                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
                          <div>
                            <SubLabel>내담자 이상형</SubLabel>
                            <Textarea
                              value={idealType1}
                              onChange={e => setIdealType1(e.target.value)}
                              placeholder="예: 자상하고 경제적으로 안정된 연상의 이성"
                              style={{minHeight:60,fontSize:12}}
                            />
                          </div>
                          <div>
                            <SubLabel>상대방 이상형</SubLabel>
                            <Textarea
                              value={idealType2}
                              onChange={e => setIdealType2(e.target.value)}
                              placeholder="예: 활발하고 표현력이 풍부한 이성"
                              style={{minHeight:60,fontSize:12}}
                            />
                          </div>
                        </div>

                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                          <div>
                            <SubLabel>내담자 연애성향</SubLabel>
                            <Textarea
                              value={romanceStyle1}
                              onChange={e => setRomanceStyle1(e.target.value)}
                              placeholder="예: 헌신적이나 집착 성향, 감정 기복 주의"
                              style={{minHeight:60,fontSize:12}}
                            />
                          </div>
                          <div>
                            <SubLabel>상대방 연애성향</SubLabel>
                            <Textarea
                              value={romanceStyle2}
                              onChange={e => setRomanceStyle2(e.target.value)}
                              placeholder="예: 자유로운 연애 선호, 속박을 싫어함"
                              style={{minHeight:60,fontSize:12}}
                            />
                          </div>
                        </div>
                      </ResultCard>
                    )}

                    {/* ── 심층 궁합 디테일 ── */}
                    {isDeep && (
                      <ResultCard>
                        <CardTitle>심층 궁합 디테일 입력</CardTitle>

                        <SubLabel>이별수 위험 연도/월</SubLabel>
                        {sepYears.map((sy,idx) => (
                          <div key={idx} style={{display:"flex",gap:6,marginBottom:8,alignItems:"center"}}>
                            <input
                              style={{width:80,padding:"8px 10px",border:"1.5px solid #DEE2E6",borderRadius:8,fontSize:13,fontFamily:"inherit"}}
                              placeholder="2030년" value={sy.year}
                              onChange={e => setSepYears(prev => prev.map((v,i) => i===idx?{...v,year:e.target.value}:v))} />
                            <input
                              style={{flex:1,padding:"8px 10px",border:"1.5px solid #DEE2E6",borderRadius:8,fontSize:13,fontFamily:"inherit"}}
                              placeholder="주의 월 (예: 5월, 10월)" value={sy.months}
                              onChange={e => setSepYears(prev => prev.map((v,i) => i===idx?{...v,months:e.target.value}:v))} />
                            <input
                              style={{flex:2,padding:"8px 10px",border:"1.5px solid #DEE2E6",borderRadius:8,fontSize:13,fontFamily:"inherit"}}
                              placeholder="사유 (예: 일지 충)" value={sy.reason}
                              onChange={e => setSepYears(prev => prev.map((v,i) => i===idx?{...v,reason:e.target.value}:v))} />
                            <Btn onClick={() => setSepYears(prev => prev.filter((_,i) => i!==idx))}
                              style={{padding:"8px 10px",color:"#C92A2A",background:"#FFF5F5",border:"1px solid #FFCCCC",borderRadius:8,fontSize:13,cursor:"pointer"}}>
                              ✕
                            </Btn>
                          </div>
                        ))}
                        <Btn type="button" $variant="outline"
                          onClick={() => setSepYears(prev => [...prev,{year:"",months:"",reason:""}])}
                          style={{marginBottom:14,fontSize:13}}>
                          + 이별수 연도 추가
                        </Btn>

                        <SubLabel>자녀운 점수</SubLabel>
                        <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center"}}>
                          <input
                            style={{width:80,padding:"8px 10px",border:"1.5px solid #DEE2E6",borderRadius:8,fontSize:14,fontFamily:"inherit"}}
                            type="number" min={0} max={100} placeholder="85"
                            value={childrenScore}
                            onChange={e => setChildrenScore(e.target.value)} />
                          <span style={{fontSize:13,color:"var(--text-secondary)"}}>점 (0~100)</span>
                        </div>

                        <SubLabel>자녀 성별 예측</SubLabel>
                        <div style={{display:"flex",gap:8,marginBottom:14}}>
                          {["아들","딸","아들·딸 모두","불확실"].map(opt => (
                            <label key={opt} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontSize:13}}>
                              <input type="radio" name="childrenGender" value={opt}
                                checked={childrenGender===opt}
                                onChange={() => setChildrenGender(opt)}
                                style={{accentColor:"#E64980"}} />
                              {opt}
                            </label>
                          ))}
                        </div>

                        <SubLabel>자녀 가능 시기</SubLabel>
                        <Textarea
                          value={childrenTiming}
                          onChange={e => setChildrenTiming(e.target.value)}
                          placeholder="예: 2028~2030년 사이, 식신운이 들어오는 시기가 가장 유리"
                          style={{minHeight:50,marginBottom:14}}
                        />

                        <SubLabel>결혼 적기 제안</SubLabel>
                        <Textarea
                          value={marriageTiming}
                          onChange={e => setMarriageTiming(e.target.value)}
                          placeholder="예: 2027년 하반기가 가장 길함 (사유: 대운과 세운의 합)"
                          style={{minHeight:60}}
                        />
                      </ResultCard>
                    )}

                    <div style={{padding:"0 16px"}}>
                      <Btn $variant="primary" onClick={saveReportConfig} disabled={busy}
                        style={{width:"100%",padding:"13px",fontSize:14}}>
                        {busy ? "저장 중..." : "리포트 구성 저장"}
                      </Btn>
                      {msg && <MsgLine style={{marginTop:8,textAlign:"center"}}>{msg}</MsgLine>}
                    </div>
                  </div>
                );
              })()}

              {/* ── 탭: 리포트 직접 수정 ─────────────────────── */}
              {tab === "edit_report" && (
                <ResultCard>
                  <CardTitle>
                    ✏️ 리포트 직접 수정
                    {isCustomMd && (
                      <span style={{ marginLeft: 10, fontSize: 11, padding: "2px 8px", background: "#e67700", color: "#fff", borderRadius: 8, fontWeight: 700 }}>
                        수정본 적용 중
                      </span>
                    )}
                  </CardTitle>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 12, lineHeight: 1.7 }}>
                    아래 내용을 자유롭게 수정한 후 저장하세요. 저장된 내용이 고객 리포트 페이지에 표시됩니다.<br/>
                    <strong>마크다운 형식</strong>으로 작성하세요 (<code>**굵게**</code>, <code>### 제목</code> 등).<br/>
                    {isCustomMd && "현재 수동 수정본이 적용 중입니다. "}
                    자동생성 리포트로 되돌리려면 <strong>초기화</strong> 버튼을 누르세요.
                  </div>
                  {reportMdBusy ? (
                    <div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>리포트 불러오는 중...</div>
                  ) : (
                    <>
                      <textarea
                        value={reportMd}
                        onChange={e => setReportMd(e.target.value)}
                        style={{
                          width: "100%", minHeight: 600, padding: "14px 16px",
                          background: "#1a1a2e", color: "#e8e8f0",
                          border: "1.5px solid #444", borderRadius: 10,
                          fontSize: 13, lineHeight: 1.8, fontFamily: "monospace",
                          resize: "vertical", boxSizing: "border-box",
                        }}
                      />
                      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                        <Btn
                          $variant="primary"
                          disabled={reportMdBusy}
                          onClick={async () => {
                            setReportMdBusy(true);
                            try {
                              await axios.put(`${API}/api/v1/admin/report-markdown/${selId}`, { markdown: reportMd });
                              setIsCustomMd(true);
                              setMsg("리포트 저장 완료!");
                              setTimeout(() => setMsg(""), 3000);
                            } catch { setMsg("저장 실패"); }
                            finally { setReportMdBusy(false); }
                          }}
                          style={{ background: "#e67700", color: "#fff", border: "none", fontWeight: 700 }}
                        >
                          💾 저장
                        </Btn>
                        <Btn
                          disabled={reportMdBusy}
                          onClick={async () => {
                            if (!window.confirm("자동생성 리포트로 초기화할까요? 수동 수정 내용이 삭제됩니다.")) return;
                            setReportMdBusy(true);
                            try {
                              await axios.delete(`${API}/api/v1/admin/report-markdown/${selId}`);
                              const r = await axios.get(`${API}/api/v1/admin/report-markdown/${selId}`);
                              setReportMd(r.data.markdown || "");
                              setIsCustomMd(false);
                              setMsg("자동생성 리포트로 복원되었습니다.");
                              setTimeout(() => setMsg(""), 3000);
                            } catch { setMsg("초기화 실패"); }
                            finally { setReportMdBusy(false); }
                          }}
                          style={{ background: "rgba(200,50,50,0.15)", color: "#FF6B6B", border: "1px solid rgba(200,50,50,0.3)" }}
                        >
                          🔄 자동생성으로 초기화
                        </Btn>
                      </div>
                    </>
                  )}
                </ResultCard>
              )}

            </>
          )}
        </Main>
      </Layout>
    </>
  );
}
